import * as types from 'constants/ActionTypes';
import store from '@/store';
import { readReferralContext, clearReferralContext, referralCoupon } from 'utils/referralContext';
import { shouldDeferReferralCoupon } from 'utils/purchase/checkoutDiscountEligibility';
import { savePendingReferralCoupon } from 'utils/purchase/referralCouponHandoff';
import { omit, intersectionBy, unionBy } from 'lodash';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { normalizeMercadoPagoBasisTheoryPayment } from 'services/mercadoPagoBasisTheoryPayment';
import { mercadoPagoBasisTheoryReceipt } from 'components/payment/MercadoPagoBasisTheory3DS/attempt';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import {
  change as changeFormField,
  reset as resetForm,
  getFormValues as getReduxFormValues,
} from 'redux-form';
import { batch } from 'react-redux';
import { getSessionId, triggerSignOutWidget } from 'utils/session';
// eslint-disable-next-line import/no-unresolved
import { trip, purchase as purchaseApi } from 'js-api-client';
import { decodePassengers, purchaseTotalByCategory } from 'utils/Reserbus';
import { getExpirationTime } from 'utils/purchase/getExpirationTime';
import { getSeatPriceZonesFromSource } from 'utils/seatPricing';
import camelizeConverter from 'utils/camelizeConverter';
import checkIfRetriesExceeded from 'utils/polls/checkIfRetriesExceeded';
import pollingPromises from 'utils/pollingPromises';
import smartSeatSelectionResult from 'utils/purchase/smartSeatSelectionResult';
import shouldAlignPaymentEngineWithSelection from 'utils/purchase/shouldAlignPaymentEngineWithSelection';
import taxpayerPayloadFields from 'utils/purchase/taxpayerPayloadFields';
import parsePurchase from 'models/parsePurchase';
import isSetSeats from 'utils/purchase/validateSetSeat';
import Cookies from 'js-cookie';
import getSeats from 'utils/purchase/getSeats';
import normalizeRegex from 'utils/normalizeRegex';
import {
  getEnabledBillingCompanyDocuments,
  isBillingCompanyDocumentOrLegacy,
} from 'utils/purchase/billingCompanyDocuments';
import { formatName } from 'utils/purchase/parsePassenger';
import { isThirdPartyRecipient } from 'utils/purchase/billingDocumentValidation';
import yuno from 'payments/core/engines/yuno';
import { isFrequentsPassengerEnabled } from 'utils/userPreferences';
import isCdsFrequentPassengerOptInEligible from 'utils/isCdsFrequentPassengerOptInEligible';
import transformProductsToAncillaries from 'utils/productsCart/transformProductsToAncillaries';
import { selectWhitelabelConfig } from 'selectors/whitelabelSelectors';
import { selectPurchase } from 'selectors/purchaseSelectors';
import { selectSelectedProducts } from 'selectors/productsCartSelectors';
import { isNativeAppWebView } from 'utils/webview';
import { getCampaignAttribution } from 'utils/campaignAttribution';
import { EXCHANGE_HOME_PATH, getExchangeLockScheduleRedirectPath } from 'utils/exchange';
import {
  trackApiError,
  trackLockSeatsError,
  trackBusSchemeError,
} from 'metrics/user-analytics/errorTracker';
import {
  getRnMixpanelBridgedTrackerId,
  getRnMixpanelNativePurchasePayloadProps,
} from 'utils/rnMixpanelNativeProps';
import { handleProductUnavailable } from './productsCart';
import {
  getUserByWalletType,
  getWalletTypeForPurchase,
  setUserTokenForAPI,
} from '../utils/loyalty';
import { setError } from '.';
import { setTerminals, setAirports, setLines, setCarriers } from './catalogs';
import customServerError from '../utils/purchase/customServerError';
import { getProcessorDocumentType } from '../utils/documentMaps';
import wayIsOpenTicket from '../utils/wayIsOpenTicket';
// eslint-disable-next-line import/no-cycle
import { setCostaPassError } from './costapass';
import { getInitialPassengers, reorderPassengers } from '../utils/passengers';
import userFingerprint from '../services/userFingerprint';
import { setSeatsBusError } from './seatsBus';
import { setUpFrequentPassengers } from './userPreferences';
import {
  hasParseablePassengerFieldError,
  backendBannerMessage,
  isPurchaserEmailValidationError,
  isBillingCompanyResolutionError,
  handleBackendFieldError,
  isProductUnavailableError,
  extractUnavailableProductCodes,
} from '../utils/purchase/parseBackendErrors';
import { CurrentLang } from '../utils/lang';
import { errorTripExchange } from './exchange';
import {
  companyCreationFailedTracker,
  serializeCarouselSession,
} from '../metrics/user-analytics/purchase';
import {
  getBfoPurchaseWays,
  getApplicableLegPricing,
  hasBfoLegContext,
  isBfoOpaqueInitialLockError,
  isBfoReturnedPurchase,
  isBfoRefreshRequiredPollError,
  isBfoSupportedJourney,
  reconcileBfoSelectedSeats,
} from '../utils/purchase/baseFareOverride';

// Raw `ApiErrors` codes as platform-purchase sends them, before `js-api-client` rewrites any of
// them. They survive on `details`/`errors[0]` and are the only way to read the backend's intent
// once the client has remapped `code` — INVALID_DISCOUNT and INTERNAL_SERVER_ERROR both reach it
// as 500.
const BACKEND_INVALID_DISCOUNT = 11;
const BACKEND_EXPIRED_DISCOUNT = 12;

const REFERRAL_COUPON_RETRYABLE_CODES = [BACKEND_INVALID_DISCOUNT, BACKEND_EXPIRED_DISCOUNT];

const alwaysCurrent = () => true;

const getOwnershipPredicate =
  (isCurrent = alwaysCurrent, ownership, attemptOwnership) =>
  () =>
    isCurrent() && (ownership?.isCurrent?.() ?? true) && (attemptOwnership?.isCurrent?.() ?? true);

const getLoadingOwnerId = (ownership, attemptOwnership) => {
  if (!attemptOwnership) return ownership?.loadingOwnerId ?? null;

  const initializationId = ownership?.loadingOwnerId;
  const attemptId = attemptOwnership.loadingOwnerId;
  if (
    typeof initializationId !== 'string' ||
    typeof attemptId !== 'string' ||
    !attemptId.startsWith(`${initializationId}:attempt:`)
  ) {
    throw new Error('Quick Purchase attempt loading owner must use its initialization namespace');
  }
  return attemptId;
};

const releaseOwnedQuickPurchaseLoading = (dispatch, ownership, attemptOwnership) => {
  const loadingOwnership = attemptOwnership || ownership;
  const initializationCanRelease = ownership?.canReleaseLoading?.() === true;
  const attemptCanRelease = attemptOwnership?.canReleaseLoading?.() === true;
  if (!loadingOwnership || (!initializationCanRelease && !attemptCanRelease)) return;
  const loadingOwnerId = getLoadingOwnerId(ownership, attemptOwnership);
  dispatch({
    type: types.RELEASE_QUICK_PURCHASE_LOADING,
    loadingOwnerId,
  });
};

/**
 * Strip UI-only meta keys from a passenger object before shipping it to the BE.
 * These keys (e.g., `lapChildExpanded`) are session-scoped form state and must
 * not appear in /purchases or /passengers payloads, where they would confuse
 * the backend contract.
 * @param {Object} passenger - Passenger object with optional `meta` field.
 * @returns {Object} Same passenger, with UI-only meta keys removed.
 */
const stripUiOnlyPassengerMeta = (passenger) => {
  if (!passenger?.meta) return passenger;

  return { ...passenger, meta: omit(passenger.meta, ['lapChildExpanded']) };
};

/**
 * Polls until next departures for bulk tickets are ready
 * @param {string} token - Purchase token
 * @returns {Function} Thunk action creator
 */
export function pollNextDepartures(token) {
  return (dispatch) => {
    dispatch({ type: types.REQUEST_NEXT_DEPARTURES });

    const polls = pollingPromises({
      data: [token],
      name: 'onReceiveNextDepartures',
      create: (polling, token) => purchaseApi.getNextDepartures(token, polling),
      finishedStatus: ['completed'],
      failedStatus: ({ status }) => !['pending', 'completed'].includes(status),
    });

    return Promise.all(polls).then((polls) => {
      polls.forEach(({ status }) => {
        checkIfRetriesExceeded(status);
      });

      const nextDepartures = polls[0].payload;

      dispatch({
        type: types.RECEIVE_NEXT_DEPARTURES,
        nextDepartures,
        receivedAt: Date.now(),
      });
    });
  };
}

export function selectInstallmentsPlan(installmentsPlan) {
  return { type: types.SELECT_MONTHLY_PLAN, installmentsPlan };
}

export function selectInstalmentsPlan(selectedPlan) {
  return { type: types.SET_SELECTED_PLAN, selectedPlan };
}

export function resetInstallmentsPlan() {
  return {
    type: types.SELECT_MONTHLY_PLAN,
    installmentsPlan: { card: '', months: 1, total: 0, monthlyPayment: 0 },
  };
}

/**
 * Sets the enable monthly selected plan state based on installments validation
 * @param {boolean} enableMonthlySelectedPlan - Whether monthly installments are enabled
 * @returns {Object} Action to set enable monthly selected plan
 */
export function setEnableMonthlySelectedPlan(enableMonthlySelectedPlan) {
  return {
    type: types.SET_ENABLE_MONTHLY_SELECTED_PLAN,
    enableMonthlySelectedPlan,
  };
}

const getAvailableBusCategories = (departs, returns = {}) => {
  let dPassengerTypes = [];
  let rPassengerTypes = [];
  let departureHasBus = false;
  let returnHasBus = false;
  let isDepartureOpenTicket = false;
  let isReturnOpenTicket = false;

  departs.oneWayPurchases.forEach(({ trip }) => {
    const { transportType, openTicket, passengerTypes = [] } = trip;
    const availableTypes = passengerTypes.filter(({ availability }) => availability > 0);

    if (transportType === 'bus' && dPassengerTypes.length === 0) {
      departureHasBus = true;
      dPassengerTypes = availableTypes;
      isDepartureOpenTicket = !!openTicket;
    } else if (transportType === 'bus') {
      dPassengerTypes = intersectionBy(dPassengerTypes, availableTypes, 'type');
    }
  });

  if (returns.oneWayPurchases) {
    returns.oneWayPurchases.forEach(({ trip }) => {
      const { transportType, openTicket, passengerTypes = [] } = trip;
      const availableTypes = passengerTypes.filter(({ availability }) => availability > 0);

      if (transportType === 'bus' && rPassengerTypes.length === 0) {
        returnHasBus = true;
        rPassengerTypes = availableTypes;
        isReturnOpenTicket = !!openTicket;
      } else if (transportType === 'bus') {
        rPassengerTypes = intersectionBy(rPassengerTypes, availableTypes, 'type');
      }
    });
  }

  if (departureHasBus && returnHasBus) {
    const { JUST_ADULT_OPEN_TICKET } = store.getState().whitelabelConfig.features;

    if (JUST_ADULT_OPEN_TICKET) {
      if (isDepartureOpenTicket && !isReturnOpenTicket) {
        return rPassengerTypes;
      }

      if (isReturnOpenTicket && !isDepartureOpenTicket) {
        return dPassengerTypes;
      }
    }

    return intersectionBy(dPassengerTypes, rPassengerTypes, 'type');
  }

  return unionBy(dPassengerTypes, rPassengerTypes, 'type');
};

const isPlanAvailable = (qualifiesForInstallments, selectedInstallmentsPlan, paymentPlans) => {
  if (qualifiesForInstallments) {
    const { card, months } = selectedInstallmentsPlan;
    return Boolean(card && paymentPlans[card][months]);
  }

  return false;
};

export function requestPayment() {
  return { type: types.REQUEST_PAYMENT };
}

export function resetPaymentCardError() {
  return { type: types.RESET_PAYMENT_CARD_ERROR };
}

export function resetPayment() {
  return { type: types.RESET_PAYMENT };
}

export function requestPurchase() {
  return { type: types.REQUEST_PURCHASE };
}

export function receivePayment(payment = {}) {
  return (dispatch, getState) => {
    let payload = camelizeKeys(payment);
    const currentPayment = getState?.()?.payment;
    const isOwnedBasisTheory =
      currentPayment?.get('cardCheckoutMode') === 'basis_theory' &&
      currentPayment?.get('paymentEngine') === 'mercadopago' &&
      currentPayment?.get('paymentType') === 'credit_card' &&
      payload.id != null &&
      String(currentPayment.get('id')) === String(payload.id);
    if (isOwnedBasisTheory) {
      const snapshot = normalizeMercadoPagoBasisTheoryPayment(payload);
      payload = snapshot
        ? mercadoPagoBasisTheoryReceipt(snapshot)
        : { id: payload.id, cardCheckoutMode: 'basis_theory' };
    } else if (
      currentPayment?.get('cardCheckoutMode') === 'basis_theory' &&
      payload.id != null &&
      String(currentPayment.get('id')) !== String(payload.id)
    ) {
      // RECEIVE_PAYMENT merges receipts; an unrelated attempt must not inherit BT ownership.
      payload.cardCheckoutMode = payload.cardCheckoutMode || 'legacy';
    }
    dispatch({
      type: types.RECEIVE_PAYMENT,
      receivedAt: Date.now(),
      payload,
    });
  };
}

export function receivePurchase(purchaseResponse, loadingOwnerId = null) {
  return (dispatch, getState) => {
    const { features } = getState().whitelabelConfig;
    const tenantSupportsConnections = features.SUPPORT_TRIPS_WITH_CONNECTIONS;
    const purchase = parsePurchase(purchaseResponse, tenantSupportsConnections);
    dispatch({
      type: types.RECEIVE_PURCHASE,
      purchase,
      tenantSupportsConnections,
      ...(loadingOwnerId != null && { loadingOwnerId }),
      receivedAt: Date.now(),
    });
    if (['completed', 'canceled'].includes(purchase?.state)) {
      smartSeatSelectionResult.disposeBooking({ token: purchase.token });
    }
  };
}

/**
 * AM-1686: fetches the purchase and updates only the `storeReferenceIssued` flag.
 * Used after a store payment attempt so the QuickCheckout edit guard reflects the
 * just-issued reference. Kept intentionally lightweight (no full receivePurchase) to avoid
 * disrupting the payment-failed state or triggering a heavy re-render.
 *
 * @param {string} token - Purchase token.
 * @returns {Function} Thunk that dispatches SET_STORE_REFERENCE_ISSUED.
 */
export function refreshStoreReferenceIssued(token) {
  return (dispatch) =>
    purchaseApi
      .get(token)
      .then((response) => {
        const value = Boolean(response?.purchase?.store_reference_issued);
        dispatch({ type: types.SET_STORE_REFERENCE_ISSUED, value });
        return value;
      })
      .catch(() => false);
}

const setFailedStatePurchaseAction = {
  type: types.SET_FAILED_STATE_PURCHASE,
};

/**
 * Releases only Quick Purchase loading flags tagged with one initialization owner.
 * This action does not publish trip details, tickets, errors or reservation outcomes.
 * @param {string} loadingOwnerId - Namespaced initialization or attempt that started the work.
 * @returns {object} Data-neutral loading release action.
 */
export function releaseQuickPurchaseLoading(loadingOwnerId) {
  return { type: types.RELEASE_QUICK_PURCHASE_LOADING, loadingOwnerId };
}

export function resetPurchase() {
  return (dispatch, getState) => {
    const purchaseToken = getState().purchase.get('token');
    if (purchaseToken != null && purchaseToken !== '') {
      smartSeatSelectionResult.disposeBooking({
        token: purchaseToken,
        onLoadingOwnerDisposed: (loadingOwnerId) =>
          dispatch(releaseQuickPurchaseLoading(loadingOwnerId)),
      });
    }
    return dispatch({ type: types.RESET_PURCHASE });
  };
}

export function updatePurchase(updating, loadingOwnerId = null) {
  return {
    type: types.UPDATE_PURCHASE,
    updating,
    ...(loadingOwnerId != null && { loadingOwnerId }),
  };
}

export function setBusCategories(busCategories, tripSlug = null) {
  return {
    type: types.SET_BUS_CATEGORIES,
    busCategories,
    tripSlug,
  };
}

/**
 * Sets the seat map for a given trip.
 * @param {string} way - The direction of the trip (e.g., 'departure', 'return').
 * @param {Object} trip - The trip object containing bus and diagramType.
 * @param {string} tripSlug - The unique identifier for the trip.
 * @param {string} [saveIn='purchase'] - The context in which the seat map is being saved.
 * @param {Array} busCategories - Categories of buses available for the trip.
 * @returns {Object} The action object with type and payload for setting the seat map.
 */
export function setSeatMap(
  way,
  trip,
  tripSlug,
  saveIn = 'purchase',
  busCategories,
  layouts,
  seats,
  trips,
) {
  const seatPriceZones = getSeatPriceZonesFromSource(trip);
  const tripDetails = trip.trip || trip;
  const { bus: tripBus, diagramType } = trip;
  const passengerTypes =
    (Array.isArray(tripDetails.passengerTypes) && tripDetails.passengerTypes.length > 0
      ? tripDetails.passengerTypes
      : trip.passengerTypes) || [];
  const tripPricingFields = ['pricing', 'departureRoundTripPricing', 'roundTripPricing'].reduce(
    (pricingFields, pricingKey) => {
      const value = tripDetails[pricingKey] ?? trip[pricingKey];
      return value == null ? pricingFields : { ...pricingFields, [pricingKey]: value };
    },
    {},
  );
  const layoutsValues = layouts ? Object.values(layouts) : [];
  /**
   * When a trip has connections, the fetchTripDetails response has a null value for the bus key (tripBus),
   * So if the trips has connections, we take the bus from the first layout at layoutsValues.
   * This first value is the bus layout for the first connection.
   * When a trip has connections the layout attribute is not useful, but the code dies if this value is not set.
   */
  const bus = layoutsValues.length ? layoutsValues[0] : tripBus;
  return {
    way,
    layout: bus,
    diagramType,
    type: saveIn === 'purchase' ? types.SET_SEAT_MAP : types.SET_SEAT_MAP_TRIPS,
    tripSlug,
    busCategories,
    layouts,
    seats,
    trips,
    seatPriceZones,
    tripPricingFields,
    passengerTypes,
  };
}

export function requestTripsDetails(way, loadingOwnerId = null) {
  return {
    type: types.REQUEST_TRIPS_DETAILS,
    way,
    ...(loadingOwnerId != null && { loadingOwnerId }),
  };
}

export function receiveTripsDetails(way, tripSlug, loadingOwnerId = null) {
  return {
    type: types.RECEIVE_TRIPS_DETAILS,
    way,
    tripSlug,
    ...(loadingOwnerId != null && { loadingOwnerId }),
  };
}

export function invalidatePurchaseTripsDetails(ways) {
  return { type: types.INVALIDATE_PURCHASE_TRIPS_DETAILS, ways };
}

export function bfoReselectionReady() {
  return { type: types.BFO_RESELECTION_READY };
}

export function bfoReselectionRefreshing(ways) {
  return { type: types.BFO_RESELECTION_REFRESHING, ways };
}

export function bfoReselectionBlocked() {
  return { type: types.BFO_RESELECTION_BLOCKED };
}

export function bfoOpaqueRecoveryAttempted() {
  return { type: types.BFO_OPAQUE_RECOVERY_ATTEMPTED };
}

export function postPassengers() {
  return { type: types.PURCHASE_POST_PASSENGERS };
}

/**
 * Sets the passengers for the purchase
 * @param {Array} passengers - List of passengers
 * @param {string} token - Purchase token
 * @returns {Object} Action to set passengers
 */
export function setPassengers(passengers, token) {
  const { purchase } = store.getState();
  const { isExchange, bulkTicket } = purchase.toJS();

  /**
   * Exchange and bulk ticket purchases require to reorder the passengers
   * As the passengers are sent in the same order as they are created, we need to reorder them
   * to avoid minor being as first passenger
   */
  if (isExchange || bulkTicket) {
    passengers = reorderPassengers(passengers);
  }

  return {
    type: types.PURCHASE_SET_PASSENGERS,
    passengers: camelizeKeys(passengers),
    token,
  };
}

export function setPaymentPlans(paymentPlans) {
  return {
    type: types.PURCHASE_SET_PAYMENT_PLANS,
    paymentPlans,
  };
}

export function receiveWalletType(walletType, flatFareAvailable) {
  return {
    type: types.RECEIVE_WALLET_TYPE,
    walletType,
    flatFareAvailable,
  };
}

/**
 * @typedef {'credit_card' | 'paypal' | 'coppel_pay' | 'oxxo' | 'efecty' | 'transfer' | 'nequi' | 'bnpl'} PaymentOption
 */

/**
 * Handles the payment option selection, this value represent the selected payment option
 * @param {PaymentOption} option - Payment option
 * @returns {Object} Event dispatch
 */
export function selectPaymentOption(option) {
  return { type: types.SELECT_PAYMENT_OPTION, option };
}

/**
 * @typedef {Object} PaymentMethod
 * @property {string} type - Payment method type
 * @property {string} engine - Payment method engine
 * @property {string} provider - Payment method provider
 */

/**
 * Function that handles the selection of a payment method
 * This payment method is a new alternative with more information about the payment method
 * @param {PaymentMethod} method - Payment method
 * @returns {Object} Event dispatch
 */
export function selectPaymentMethod(method) {
  return { type: types.SELECT_PAYMENT_METHOD, method };
}

/**
 * Function that handles the creation of a new purchase
 * @param {String} dSlug - Departure slug
 * @param {String} rSlug - Return slug
 * @param {String} passengers - Passenger selection.
 * @param {String | Number} [seenPrice] - Seen price
 * @param {String} [adAttribution] - Ad attribution
 * @param {Boolean} [isExchange] - Is exchange
 * @param {Object} [exchangeData] - Exchange data. `exchangeData.transporter` (task 8.6) is the
 *   explicit transporter abbr chosen via the IAMSA line selector, if any
 * @param {Boolean} [redirect] - Redirect
 * @param {String} [redirectedFrom] - Redirected from brand
 * @param {Object} [tripInfo] - Trip info
 * @param {Object} [preSelectedSeats] - Preselected seat data.
 * @param {Boolean} [quickPurchase] - Whether purchase is for quick checkout.
 * @param {String} [externalCouponCode] - Explicit external coupon code.
 * @param {Object} [options] - Purchase surface intent for referral coupon handling.
 * @returns Event dispatch
 */
export function newPurchase(
  dSlug,
  rSlug,
  passengers = 'A1',
  seenPrice,
  adAttribution,
  isExchange = false,
  exchangeData = {},
  redirect = false,
  redirectedFrom = '',
  tripInfo,
  preSelectedSeats,
  quickPurchase = false,
  externalCouponCode,
  options = {},
) {
  return (dispatch) => {
    const {
      search,
      whitelabelConfig: { env, features },
      carouselSession,
    } = store.getState();
    const decodedPassengers = decodePassengers(passengers);
    const referral = readReferralContext();
    const manualCoupon = search.get('couponCode', null);
    const externalCoupon = referralCoupon(externalCouponCode, manualCoupon, referral);
    const usesReferralCoupon =
      externalCouponCode == null && !manualCoupon?.trim() && referral?.discountCode;
    const deferReferralCoupon =
      usesReferralCoupon &&
      referral.confirmed &&
      shouldDeferReferralCoupon({
        features,
        purchaseSurface: options.purchaseSurface,
        isExchange,
      });
    const { operationNumbers, nit, document, email, transporter } = exchangeData;
    const userFingerprintValue = userFingerprint.getFingerprint();

    const googleAnalyticsID = Cookies.get('_ga'); // Google Analytics ID to track offline purchases
    const rnNativePayloadProps = getRnMixpanelNativePurchasePayloadProps();
    const payload = {
      departs: dSlug,
      returns: rSlug,
      passenger_selection: decodedPassengers,
      seen_price: seenPrice && Number(seenPrice),
      ad_attribution: adAttribution === 'true',
      device_fingerprint: userFingerprintValue,
      tracker_id: getRnMixpanelBridgedTrackerId(),
      webview: isNativeAppWebView(),
      ...rnNativePayloadProps,
      ...(features?.SEND_CAMPAIGN_ATTRIBUTION_TO_PURCHASE && getCampaignAttribution()),
      ga_client_id: googleAnalyticsID,
      ...(env.riskified && env.riskified.enabled && { riskified_beacon_id: getSessionId() }),
      locale:
        typeof window !== 'undefined' && window.localStorage
          ? window.localStorage.getItem('i18nextLng') || 'es-MX'
          : 'es-MX',
      external_coupon_code: deferReferralCoupon ? null : externalCoupon,
      ...(referral && { referral_slug: referral.slug }),
      ...(isExchange && {
        operation_number: operationNumbers,
        exchange_ticket_keys: operationNumbers,
        nit,
        document,
        email,
        // Task 8.6 (openspec/changes/iamsa-b2c-ticket-exchange, platform-sre): backend already
        // validates this matches `trip.transporter_id` (ExchangeValidator#trip_transporter_match,
        // platform-purchase#3071) — sending it here is what lets that check actually run instead
        // of falling back to `tenant.exchange_transporter` (D3), which `reservamos` deliberately
        // leaves unset.
        ...(transporter && { transporter }),
      }),
      redirected_from: redirectedFrom,
      redirect,
      ...(tripInfo && decamelizeKeys(tripInfo)),
      ...(preSelectedSeats && decamelizeKeys({ ...preSelectedSeats })),
      ...(quickPurchase && { quick_purchase: true }),
      ...(carouselSession && {
        days_carousel_track_props: decamelizeKeys(serializeCarouselSession(carouselSession)),
      }),
    };

    // The user token is set in the requests
    setUserTokenForAPI();
    if (isFrequentsPassengerEnabled()) {
      dispatch(setUpFrequentPassengers());
    }
    dispatch(resetPurchase());
    dispatch(requestPurchase());

    if (isExchange) {
      dispatch({ type: types.SET_EXCHANGE_OPERATION });
    }

    return purchaseApi
      .create(payload)
      .catch((error) => {
        if (
          !usesReferralCoupon ||
          deferReferralCoupon ||
          !REFERRAL_COUPON_RETRYABLE_CODES.includes(error.details?.code)
        )
          throw error;
        return purchaseApi.create({ ...payload, external_coupon_code: null });
      })
      .then((purchaseResponse) => {
        const {
          terminals,
          airports,
          lines,
          carriers,
          purchase: { departs, returns, token },
        } = camelizeKeys(purchaseResponse, camelizeConverter);
        if (token && referral) {
          if (deferReferralCoupon && readReferralContext()?.visitId === referral.visitId) {
            savePendingReferralCoupon(token, referral);
          }
          clearReferralContext(referral);
        }
        const busCategories = getAvailableBusCategories(departs, returns);

        dispatch(setTerminals(terminals));
        dispatch(setAirports(airports));
        dispatch(setLines(lines));
        dispatch(setCarriers(carriers));
        dispatch(setBusCategories(busCategories));
        dispatch(receivePurchase(purchaseResponse));

        return token;
      })
      .catch((reason) => {
        const { code, message } = reason;
        const codeType = 200;
        if (!redirect) {
          // eslint-disable-next-line prettier/prettier
          dispatch(
            setError(
              codeType,
              'trip_not_available',
              'warning',
              true,
              customServerError(code, message),
              isExchange && EXCHANGE_HOME_PATH,
            ),
          );
        }
        dispatch(setFailedStatePurchaseAction);
        if (isExchange) dispatch(errorTripExchange(true));
        trackApiError({
          status: code ?? reason?.status,
          message: message ?? reason?.message,
          url: 'purchase.create',
          funnel_step: 'Purchase Attempt',
        });
        console.error(reason);
      });
  };
}

export function getPurchase(
  token,
  update = false,
  {
    preservePaymentOptionSelection = false,
    isCurrent = alwaysCurrent,
    ownership,
    attemptOwnership,
  } = {},
) {
  return (dispatch) => {
    const ownsCurrent = getOwnershipPredicate(isCurrent, ownership, attemptOwnership);
    const superseded = () => {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return { superseded: true };
    };
    if (!ownsCurrent()) return Promise.resolve(superseded());

    const {
      whitelabelConfig: {
        env: { defaultPaymentMethod, defaultPaymentOption },
      },
      payment,
    } = store.getState();

    if (update) {
      dispatch(updatePurchase(true, getLoadingOwnerId(ownership, attemptOwnership)));
    } else {
      dispatch(requestPurchase());
    }

    return purchaseApi
      .get(token)
      .then((purchaseResponse) => {
        if (!ownsCurrent()) return superseded();

        const {
          terminals,
          airports,
          lines,
          carriers,
          purchase: {
            departs,
            lastPaymentId,
            returns,
            paymentMethods,
            paymentEngine: purchasePaymentEngine,
          },
        } = camelizeKeys(purchaseResponse, camelizeConverter);

        const busCategories = getAvailableBusCategories(departs, returns);
        const defaultMatchingMethod =
          paymentMethods.find((method) => {
            return (
              method.type === defaultPaymentMethod.type &&
              method.engine === defaultPaymentMethod.engine &&
              method.provider === defaultPaymentMethod.provider
            );
          }) || {};

        // When refreshing an in-progress purchase (e.g. the relock after editing passengers
        // in Quick Checkout), keep the payment option/method the user already chose if it's
        // still offered, instead of resetting to the tenant default. Resetting would flip
        // selectedPaymentOption, remounting the keyed PaymentForm and wiping entered card data
        // mid-payment. Only opted-in callers (preservePaymentOptionSelection) get this.
        // selectedPaymentMethod is stored as a plain JS object, so read it with getIn instead
        // of serializing the whole purchase tree with toJS.
        const currentMethod = store.getState().purchase.getIn(['selectedPaymentMethod']);
        // Preserve the user's choice using the refreshed method object from the new response
        // (not the stale one from the old store state), so any backend metadata updates apply.
        const preservedMatchingMethod =
          preservePaymentOptionSelection && currentMethod
            ? paymentMethods.find(
                (method) =>
                  method.type === currentMethod.type &&
                  method.engine === currentMethod.engine &&
                  method.provider === currentMethod.provider,
              )
            : null;
        const currentMethodStillAvailable = Boolean(preservedMatchingMethod);
        const paymentMethod = preservedMatchingMethod || defaultMatchingMethod;

        dispatch(setTerminals(terminals));
        dispatch(setAirports(airports));
        dispatch(setLines(lines));
        dispatch(setCarriers(carriers));
        dispatch(setBusCategories(busCategories));
        dispatch(selectPaymentMethod(paymentMethod));
        // Skip the option reset when preserving: the receivePurchase reducer keeps the current
        // selectedPaymentOption, so dispatching the tenant default here would override it.
        if (!currentMethodStillAvailable) {
          dispatch(selectPaymentOption(defaultPaymentOption));
        }
        dispatch(
          receivePurchase(
            purchaseResponse,
            update ? getLoadingOwnerId(ownership, attemptOwnership) : null,
          ),
        );

        if (
          shouldAlignPaymentEngineWithSelection({
            selectedMethodEngine: paymentMethod.engine,
            purchasePaymentEngine,
            lastPaymentId,
          })
        ) {
          dispatch(selectPaymentMethod(paymentMethod));
        }

        const currentRoute = window.location.pathname;
        const { id: currrentLastPaymentId, status } = payment.toJS();
        if (
          status === 'failed' &&
          lastPaymentId === currrentLastPaymentId &&
          currentRoute.includes('checkout')
        )
          return;

        if (lastPaymentId) {
          if (!ownsCurrent()) return superseded();
          dispatch(requestPayment());
          purchaseApi
            .getPayment(token, lastPaymentId)
            .then((paymentResponse) => {
              if (!ownsCurrent()) {
                releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
                return;
              }
              dispatch(receivePayment(paymentResponse));
            })
            .catch((reason) => {
              if (!ownsCurrent()) {
                releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
                return;
              }
              dispatch(setError(304, 'error_when_looking_for_payment', 'error', true));
              dispatch(receivePayment());
              throw new Error(`Error 304: Failed to get payment. ${reason.message}`);
            });
        }
      })
      .catch((reason) => {
        if (!ownsCurrent()) return superseded();
        dispatch(setError(201, 'error_when_generating_purchase'));
        trackApiError({
          status: reason?.code || reason?.status,
          message: reason?.message,
          url: 'purchase.create',
        });
        console.error(reason);
      });
  };
}

/**
 * Fetches a purchase by token for the quick purchase flow and validates that the
 * expected purchase actually loaded into the store.
 *
 * @param {string} token - Purchase token to fetch.
 * @returns {Function} Thunk that resolves when the purchase loaded, or rejects with
 *   a `QUICK_PURCHASE_NOT_FOUND` error when the loaded purchase does not match `token`.
 */
export function fetchQuickPurchaseByToken(token) {
  return (dispatch, getState) => {
    dispatch(resetPurchase());
    return dispatch(getPurchase(token)).then(() => {
      const { purchase } = getState();
      const loadedToken = purchase.get('token');
      const purchaseLoaded = purchase.get('loaded');

      if (loadedToken !== token || !purchaseLoaded) {
        return Promise.reject(new Error('QUICK_PURCHASE_NOT_FOUND'));
      }

      return null;
    });
  };
}

export function unlockSeats({
  purchaseToken,
  forceUnlock,
  shouldUpdateState,
  dispatch,
  propagateError = false,
}) {
  const { pathname } = window.location;
  if (!forceUnlock && (pathname.includes('purchase') || pathname.includes('payment'))) return;

  smartSeatSelectionResult.disposeBooking({ token: purchaseToken });
  if (shouldUpdateState) dispatch(updatePurchase(true));
  return purchaseApi
    .unlockSeats(purchaseToken)
    .then((purchaseResponse) => {
      if (shouldUpdateState && dispatch && purchaseResponse) {
        dispatch(receivePurchase(purchaseResponse));
      }
    })
    .catch((reason) => {
      console.error(reason);
      if (propagateError) throw reason;
    });
}

export function updatingWalletType(updating) {
  return {
    type: types.UPDATING_WALLET_TYPE,
    updating,
  };
}

export function updateWalletType({
  purchaseToken,
  walletType,
  needsUnlockSeats,
  needsDisableFlatFare,
}) {
  return (dispatch) => {
    const payload = {
      wallet_type: walletType,
      ...(needsDisableFlatFare && { wants_flat_fare: false }),
    };
    dispatch(updatingWalletType(true));
    purchaseApi
      .update(purchaseToken, payload)
      .then((response) => {
        const {
          purchase: { walletType, flatFareAvailable },
        } = camelizeKeys(response);
        dispatch(receiveWalletType(walletType, flatFareAvailable));
        if (needsUnlockSeats) {
          unlockSeats({ purchaseToken, forceUnlock: true, shouldUpdateState: true, dispatch });
        }
      })
      .catch((reason) => {
        dispatch(setError(202, reason.message, 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(updatingWalletType(false));
      });
  };
}

/**
 * Returns and object with the bus layouts by trip slug.
 * The resultant object would have the tripSlug for each seat.
 *
 * @param {*} trips
 * @returns {Object} - Object with the bus layouts by trip slug.
 *
 * @example
 * const layouts = getConnectionTripsBusLayouts(trips);
 * console.log(layouts);
 * // {
 * // "trip-slug1": [[ [ {category: "seat", tripSlug: "trip-slug1"}], [{categoty: "hallway"}] ]],
 * // "trip-slug2": [[ [], [] ]],
 * // }
 */
const getConnectionTripsBusLayouts = (trips) => {
  return trips.reduce((acc, trip) => {
    const {
      bus,
      trip: { id: slug },
    } = trip;
    /**
     * Adding the tripSlug to the seats object in the bus layout.
     * It doesn't modifies the bus object structure, it just adds the tripSlug to the seats.
     */
    bus
      .flat(2)
      .filter((item) => item.number)
      .forEach((item) => {
        item.tripSlug = slug;
      });
    acc[slug] = bus;
    return acc;
  }, {});
};

/**
 * Returns an object with the seats by trip slug.
 * The resultant object would have the tripSlug for each seat.
 *
 * @param {*} trips
 * @returns - Object with the bus seats by trip slug.
 *
 * @example
 * const seats = getConnectionTripsSeats(trips);
 * console.log(seats);
 * // {
 * // "trip-slug1": [{ number: "15", tripSlug: "trip-slug1" }, { number: "16", tripSlug: "trip-slug1" }],
 * // "trip-slug2": [{ number: "1", tripSlug: "trip-slug2" }, { number: "2", tripSlug: "trip-slug2" }],
 * // }
 */
const getConnectionTripsSeats = (trips) => {
  return trips.reduce((acc, trip) => {
    const {
      bus,
      trip: { id: slug },
    } = trip;
    acc[slug] = getSeats(bus, slug);
    return acc;
  }, {});
};

/**
 * Fetches detailed information about trips based on departure and return fragments.
 * When provided, purchaseToken is sent as purchase_token to the trip details service (e.g. for exchange flow).
 * @param {Object} params - The parameters for fetching trip details.
 * @param {Array} params.departureFragments - Array of departure trip fragments.
 * @param {Array} [params.returnFragments=[]] - Array of return trip fragments (optional).
 * @param {string} [params.purchaseToken] - Optional. In exchange flow, current purchase token; sent as purchase_token to the trip details service.
 * @param {string} [params.saveIn='purchase'] - Key in Redux state where fetched trip details are stored.
 * @param {Function} [params.isCurrent] Optional async ownership predicate for legacy callers.
 * @param {object} [params.ownership] Quick Purchase generation handle. Its primitive owner id
 * tags loading state, and only a latest invalidated handle may release matching flags.
 * @param {object} [params.attemptOwnership] Optional result/request-scoped capability layered on
 * the generation. Its predicate is conjunctive; its owner id must come from
 * `createQuickPurchaseAttemptLoadingOwnerId` so reducer-wide release remains globally disjoint.
 * @param {Array<string>} [params.ways] - Optional ways whose purchase-details lifecycle
 *   should be updated. The default keeps the existing two-way lifecycle.
 * @param {Object} [params.requestOptionsByWay] - Optional per-way request options so departure
 *   and return ids receive independent option objects: `{ departs?: { pricingVariant },
 *   returns?: { pricingVariant } }`. `pricingVariant` is sent as `pricing_variant` only for a
 *   pre-purchase result leg and never together with a purchase token.
 */
export function fetchTripsDetails({
  departureFragments,
  returnFragments = [],
  saveIn = 'purchase',
  purchaseToken,
  isCurrent = alwaysCurrent,
  ownership,
  attemptOwnership,
  ways,
  requestOptionsByWay = {},
}) {
  return (dispatch) => {
    const ownsCurrent = getOwnershipPredicate(isCurrent, ownership, attemptOwnership);
    const superseded = () => {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return { superseded: true };
    };
    const loadingOwnerId = getLoadingOwnerId(ownership, attemptOwnership);
    if (!ownsCurrent()) return Promise.resolve(superseded());

    const seatsBus = store.getState().seatsBus.toJS();
    const { features } = store.getState().whitelabelConfig;

    /**
     * @todo Remove this feature flag
     * We can determine if the trip has connections using the trip fragments
     * so we don't really need this feature flag.
     */
    const tenantSupportsTripsWithConnections = features.SUPPORT_TRIPS_WITH_CONNECTIONS;
    const saveInPurchase = saveIn === 'purchase';
    const requestedWays = ways || ['departs', 'returns'];

    let tripIds = [...departureFragments, ...returnFragments].map((owp) => owp.id);

    /**
     * Result pricing context per trip id. A purchase-scoped request is authoritative and
     * never carries a variant; a result leg carries the variant its way asked for.
     */
    const pricingVariantByTripId = {};
    if (!purchaseToken) {
      departureFragments.forEach(({ id }) => {
        pricingVariantByTripId[id] = requestOptionsByWay.departs?.pricingVariant;
      });
      returnFragments.forEach(({ id }) => {
        pricingVariantByTripId[id] = requestOptionsByWay.returns?.pricingVariant;
      });
    }
    const getPricingVariant = (tripId) => pricingVariantByTripId[tripId] || undefined;

    if (isSetSeats(seatsBus, tripIds, pricingVariantByTripId) && !saveInPurchase) {
      return;
    }

    /**
     * When the trip has connections, the purchase response has more than one fragment (trip connections count + 1)
     * The +1 is the "general" trip, which has the primary origin and final destination.
     * The id from this trip is the one that we need to use in order to fetch the bus layouts of the connection trips.
     * This "general" trip always comes in the first position of the fragments array, so we're getting its ID with this code.
     * In that way, we only fetch details of the "general" trip.
     */
    if (tenantSupportsTripsWithConnections) {
      tripIds = [];
      if (departureFragments.length) tripIds.push(departureFragments[0].id);
      if (returnFragments.length) tripIds.push(returnFragments[0].id);
    }

    /**
     * Dispatching the action to indicate that the bus data is being refreshed.
     * This is useful to show a loading spinner while the data is being fetched.
     */
    if (!ownsCurrent()) return Promise.resolve(superseded());
    dispatch(updatePurchase(true, loadingOwnerId));

    const polls = pollingPromises({
      data: tripIds,
      name: 'onReceiveTrip',
      create: (polling, tripId) => {
        const pricingVariant = getPricingVariant(tripId);
        return trip.getWithBus(
          tripId,
          {
            include: ['bus'],
            ...(purchaseToken && { purchase_token: purchaseToken }),
            ...(pricingVariant && { pricing_variant: pricingVariant }),
          },
          polling,
        );
      },
    });
    if (saveInPurchase) {
      if (!ownsCurrent()) return Promise.resolve(superseded());
      requestedWays.forEach((way) => dispatch(requestTripsDetails(way, loadingOwnerId)));
    }

    return Promise.all(polls)
      .then((rawTrips) => {
        if (!ownsCurrent()) return superseded();

        const trips = camelizeKeys(rawTrips, camelizeConverter).map((rawTrip) => rawTrip.payload);
        const formattedTrips = {
          oneWayPurchases: trips.map(({ trip }) => ({ trip })),
        };
        const busCategories = getAvailableBusCategories(formattedTrips);
        const totalFragmentsCount = trips.length;
        const departureFragmentsCount = tenantSupportsTripsWithConnections
          ? Number(departureFragments.length > 0)
          : departureFragments.length;
        const tripsByWay = {
          departs: trips.slice(0, departureFragmentsCount),
          returns: trips.slice(departureFragmentsCount, totalFragmentsCount),
        };

        const detailsByWay = {};
        ['departs', 'returns'].forEach((way) => {
          if (!ownsCurrent()) {
            releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
            return;
          }
          if (!requestedWays.includes(way)) return;
          const wayTrips = tripsByWay[way];
          const firstTrip = wayTrips[0];
          const tripSlug = firstTrip && firstTrip.trip.id;
          const hasConnections = firstTrip ? firstTrip.hasConnections : false;
          const bus = hasConnections ? {} : firstTrip?.bus;
          const usableSeats = Array.isArray(bus) ? getSeats(bus) : {};
          detailsByWay[way] = {
            tripSlug,
            usable: Object.keys(usableSeats).some((key) => key !== 'categories'),
          };
          if (wayTrips.length === 1 && bus) {
            const trip = firstTrip;
            // A result map remembers the pricing context it was quoted for (RM-1201).
            const resultPricingVariant = saveInPurchase
              ? undefined
              : requestOptionsByWay[way]?.pricingVariant;
            const withResultContext = (action) =>
              resultPricingVariant ? { ...action, pricingVariant: resultPricingVariant } : action;
            if (hasConnections) {
              const layouts = getConnectionTripsBusLayouts(trip.trips);
              const seats = getConnectionTripsSeats(trip.trips);
              dispatch(
                withResultContext(
                  setSeatMap(
                    way,
                    firstTrip,
                    tripSlug,
                    saveIn,
                    busCategories,
                    layouts,
                    seats,
                    trip.trips,
                  ),
                ),
              );
            } else {
              dispatch(
                withResultContext(setSeatMap(way, firstTrip, tripSlug, saveIn, busCategories)),
              );
            }
          }
          if (saveInPurchase) {
            dispatch(setBusCategories(busCategories, tripSlug));
            dispatch(receiveTripsDetails(way, tripSlug, loadingOwnerId));
          }
        });
        return { ok: true, detailsByWay };
      })
      .catch((reason) => {
        if (!ownsCurrent()) return superseded();
        if (!saveInPurchase) {
          dispatch(
            setSeatsBusError({ tripId: tripIds[0], pricingVariant: getPricingVariant(tripIds[0]) }),
          );
        }
        dispatch(setError(203, 'error_when_consulting_a_trip', 'error', saveInPurchase));
        trackBusSchemeError({
          code: reason?.code || reason?.status,
          message: reason?.message,
          tripId: tripIds[0],
        });
        console.error(reason);
        return { ok: false, reason, detailsByWay: {} };
      })
      .finally(() => {
        if (ownsCurrent()) dispatch(updatePurchase(false, loadingOwnerId));
        else releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      });
  };
}

export function deletePassenger(token, passengerId) {
  return (dispatch) => {
    dispatch(updatePurchase(true));

    return purchaseApi
      .deletePassenger(token, passengerId)
      .then(({ passengers }) => {
        dispatch(setPassengers(decamelizeKeys(passengers), token));
      })
      .catch((reason) => {
        dispatch(setError(204, reason.message, 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(updatePurchase(false));
      });
  };
}
/**
 * Selects seats for a specified trip way and saves them based on the context.
 * @param {string} way - The direction of the trip (e.g., 'departure', 'return').
 * @param {Array} seats - An array of seat identifiers to be selected.
 * @param {string} saveIn - The context in which the seat selection is being saved ('purchase' or other).
 * @returns {Object} The action object with type and payload for seat selection.
 */
export function selectSeats(way, seats, saveIn = 'purchase', tripSlug) {
  return {
    type: saveIn !== 'purchase' ? types.SELECT_SEATS_TRIPS : types.SELECT_SEATS,
    way,
    seats,
    tripSlug,
  };
}

export function updateSeat(way, seat, saveIn = 'purchase', tripSlug) {
  return {
    type: saveIn !== 'purchase' ? types.UPDATE_SEAT_TRIPS : types.UPDATE_SEAT,
    way,
    seat,
    tripSlug,
  };
}

export function finishSeatSelection() {
  return (dispatch, getState) => {
    const { purchase, whitelabelConfig } = getState();
    const { features } = whitelabelConfig;
    const { allowsSeatSelection, departs, returns, passengerSelection } = purchase.toJS();
    const walletType = getWalletTypeForPurchase();
    const mainUser = getUserByWalletType(walletType);

    const passengersWithSeats = getInitialPassengers({
      allowsSeatSelection,
      departureSelectedSeats: departs.selectedSeats,
      returnSelectedSeats: returns?.selectedSeats,
      passengerSelection,
      departureTrips: departs.trips,
      returnTrips: returns?.trips || [],
    });

    dispatch({ type: types.FINISHED_SEAT_SELECTION, features, mainUser, passengersWithSeats });
  };
}

export function clearSeats(way) {
  return (dispatch) => {
    dispatch(selectSeats(way, []));
  };
}

export function requestTickets(way, loadingOwnerId = null) {
  return {
    type: types.REQUEST_TICKETS,
    way,
    ...(loadingOwnerId != null && { loadingOwnerId }),
  };
}

export function receiveTickets(way, tickets, error = null, loadingOwnerId = null) {
  return {
    type: types.RECEIVE_TICKETS,
    way,
    tickets,
    error,
    ...(loadingOwnerId != null && { loadingOwnerId }),
  };
}

const cloneReservationValue = (value) => {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(cloneReservationValue);
  return Object.keys(value).reduce((clone, key) => {
    clone[key] = cloneReservationValue(value[key]);
    return clone;
  }, {});
};

/**
 * Reports an immutable lock outcome without allowing analytics to affect booking behavior.
 * @param {Function} observer - Optional reservation outcome observer.
 * @param {object} outcome - Actual serialized request and polling receipt projection.
 * @returns {void}
 */
const safelyObserveReservationOutcome = (observer, outcome) => {
  if (typeof observer !== 'function') return;
  try {
    const result = observer(cloneReservationValue(outcome));
    if (result && typeof result.then === 'function') {
      Promise.resolve(result).catch(() => undefined);
    }
  } catch (_error) {
    // Reservation observers are analytics-only and must never alter booking behavior.
  }
};

/**
 * Refreshes current trip layouts under an optional Quick Purchase generation.
 * @param {object} [options] Refresh ownership options.
 * @param {Function} [options.isCurrent] Compatibility ownership predicate.
 * @param {object} [options.ownership] Generation handle used by nested loading cleanup.
 * @param {object} [options.attemptOwnership] Optional same-generation request capability.
 * @returns {Function} Redux thunk.
 */
export function refreshBus({ isCurrent = alwaysCurrent, ownership, attemptOwnership } = {}) {
  return (dispatch) => {
    const ownsCurrent = getOwnershipPredicate(isCurrent, ownership, attemptOwnership);
    if (!ownsCurrent()) {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return;
    }
    const { departs, returns = {} } = store.getState().purchase.toJS();
    if (!ownsCurrent()) {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return;
    }
    return dispatch(
      fetchTripsDetails({
        departureFragments: departs.fragments,
        returnFragments: returns.fragments,
        isCurrent: ownsCurrent,
        ownership,
        attemptOwnership,
      }),
    );
  };
}

/**
 * Narrow BFO reselection recovery coordinator (RM-1201). Composed only from existing
 * purchase primitives: it retains the purchase, releases currently locked tickets when
 * applicable, clears both legs' ticket and seat selections, shows the neutral localized
 * copy, and requests fresh purchase-scoped availability through the existing
 * trip-details lifecycle so reselection restarts at departure through the existing
 * successful flow. It never touches passenger, purchaser, or payment state.
 * @param {Object} [options] - Coordinator options.
 * @param {boolean} [options.releaseLocks=true] - Whether locked tickets may exist and
 *   must be released (false for post-creation reconciliation, before any lock).
 * @param {boolean} [options.signalNavigation=true] - Whether the existing passenger
 *   lifecycle should restart at departure after a usable refresh.
 * @param {Array<string>} [options.ways] - Applicable BFO legs captured by reconciliation.
 * @returns {Function} Thunk resolving to `recovered`, `blocked`, or `not_applicable`.
 */
export function recoverFromBfoPriceRefresh({
  releaseLocks = true,
  signalNavigation = true,
  ways,
} = {}) {
  return (dispatch) => {
    const state = store.getState();
    const purchase = state.purchase.toJS();
    const { token, departs, returns = {} } = purchase;
    const { features, env } = state.whitelabelConfig;
    const applicableWays = ways || getBfoPurchaseWays(purchase);

    if (
      !isBfoSupportedJourney({ purchase, features, brand: env?.brand }) ||
      !applicableWays.length
    ) {
      return Promise.resolve('not_applicable');
    }

    const unlock = releaseLocks
      ? Promise.resolve(
          unlockSeats({
            purchaseToken: token,
            forceUnlock: true,
            shouldUpdateState: true,
            dispatch,
            propagateError: true,
          }),
        ).then(
          () => true,
          () => false,
        )
      : Promise.resolve(true);

    dispatch(bfoReselectionRefreshing(applicableWays));
    return unlock.then((didUnlock) => {
      if (!didUnlock) {
        batch(() => {
          dispatch(updatePurchase(false));
          dispatch(setError(204, 'error_unlocking_seats', 'error', false));
          dispatch(bfoReselectionBlocked());
        });
        return 'blocked';
      }

      batch(() => {
        dispatch(receiveTickets('departs', []));
        dispatch(receiveTickets('returns', []));
        dispatch(clearSeats('departs'));
        dispatch(clearSeats('returns'));
        dispatch(invalidatePurchaseTripsDetails(applicableWays));
      });

      return dispatch(
        fetchTripsDetails({
          departureFragments: applicableWays.includes('departs') ? departs?.fragments || [] : [],
          returnFragments: applicableWays.includes('returns') ? returns?.fragments || [] : [],
          purchaseToken: token,
          ways: applicableWays,
        }),
      ).then((result) => {
        const hasUsableAuthority =
          result?.ok && applicableWays.every((way) => result.detailsByWay?.[way]?.usable);
        if (!hasUsableAuthority) {
          batch(() => {
            if (result?.ok) {
              dispatch(setError(204, 'trip_no_longer_available', 'warning', false));
            }
            dispatch(bfoReselectionBlocked());
          });
          return 'blocked';
        }

        batch(() => {
          dispatch(setError(204, 'price_changed_select_seats_again', 'warning', false));
          if (signalNavigation) {
            dispatch(bfoReselectionReady());
          } else {
            dispatch({ type: types.CLEAR_TICKETS_ERROR_TYPE });
          }
        });
        return 'recovered';
      });
    });
  };
}

/**
 * Post-creation BFO reconciliation for seats-on-results purchases (RM-1201). When the
 * journey is supported and a leg carries canonical BFO context on either side of the
 * purchase boundary, it fetches fresh purchase-token-scoped trip details for every
 * applicable leg, awaits completion, reconciles every provisional selection against
 * the authoritative map, and replaces successful matches with the authoritative seat
 * objects. Any mismatch fails closed into the narrow BFO recovery before passengers.
 * Ordinary and excluded-flow purchases return null synchronously with no extra request.
 * @returns {Function} Thunk returning null when BFO does not apply, otherwise a promise
 *   resolving to 'reconciled', 'recovered', or 'blocked' (trip-details failure keeps
 *   its existing outcome).
 */
export function reconcileBfoPreselectedSeats() {
  return (dispatch) => {
    const state = store.getState();
    const purchase = state.purchase.toJS();
    const seatsBus = state.seatsBus.toJS();
    const { features, env } = state.whitelabelConfig;

    if (!isBfoSupportedJourney({ purchase, features, brand: env?.brand })) return null;

    const isRoundTrip = Boolean(purchase.roundTrip);
    const legs = ['departs', 'returns']
      .map((way) => {
        const leg = purchase[way];
        const fragment = leg?.fragments?.[0];
        if (!fragment || !leg.selectedSeats?.length) return null;

        const provisionalPricing = getApplicableLegPricing(
          seatsBus[fragment.id]?.tripPricingFields,
          { way, isRoundTrip },
        );
        const purchasePricing = getApplicableLegPricing(fragment, { way, isRoundTrip });
        if (!hasBfoLegContext({ provisionalPricing, purchasePricing })) return null;

        return { way, provisionalPricing, purchasePricing, selectedSeats: leg.selectedSeats };
      })
      .filter(Boolean);

    if (!legs.length) return null;

    const applicableWays = legs.map(({ way }) => way);
    dispatch(invalidatePurchaseTripsDetails(applicableWays));
    return dispatch(
      fetchTripsDetails({
        departureFragments: applicableWays.includes('departs')
          ? purchase.departs?.fragments || []
          : [],
        returnFragments: applicableWays.includes('returns')
          ? purchase.returns?.fragments || []
          : [],
        purchaseToken: purchase.token,
        ways: applicableWays,
      }),
    ).then((detailsResult) => {
      const hasUsableAuthority =
        detailsResult?.ok &&
        applicableWays.every((way) => detailsResult.detailsByWay?.[way]?.usable);
      if (!hasUsableAuthority) {
        if (detailsResult?.ok) {
          dispatch(setError(204, 'trip_no_longer_available', 'warning', false));
        }
        dispatch(bfoReselectionBlocked());
        return 'blocked';
      }

      const freshPurchase = store.getState().purchase.toJS();

      const outcomes = legs.map((leg) => {
        const freshLeg = freshPurchase[leg.way] || {};
        const authoritativeSeats = freshLeg.seats || {};
        const hasSeats = Object.keys(authoritativeSeats).some((key) => key !== 'categories');
        // A failed or empty purchase-scoped map keeps its existing trip-details outcome.
        if (!hasSeats) return { status: 'blocked' };

        const authoritativePricing = getApplicableLegPricing(freshLeg.tripPricingFields, {
          way: leg.way,
          isRoundTrip,
        });
        const result = reconcileBfoSelectedSeats({
          selectedSeats: leg.selectedSeats,
          provisionalPricing: leg.provisionalPricing,
          purchasePricing: leg.purchasePricing,
          authoritativePricing,
          authoritativeSeats,
        });
        return result.ok
          ? { status: 'ok', way: leg.way, seats: result.seats }
          : { status: 'mismatch' };
      });

      if (outcomes.some((outcome) => outcome.status === 'blocked')) {
        dispatch(setError(204, 'trip_no_longer_available', 'warning', false));
        dispatch(bfoReselectionBlocked());
        return 'blocked';
      }
      if (outcomes.some((outcome) => outcome.status === 'mismatch')) {
        return dispatch(
          recoverFromBfoPriceRefresh({
            releaseLocks: false,
            signalNavigation: false,
            ways: applicableWays,
          }),
        );
      }

      outcomes.forEach((outcome) => dispatch(selectSeats(outcome.way, outcome.seats)));
      return 'reconciled';
    });
  };
}

/**
 * For purchase with connections, each passenger can have multiple tickets for each trip (departure and return).
 * This method asignates seats to each passenger and creates a ticket for each selected seat.
 * Each ticket has the following structure:
 *
 * ```js
 *  {
 *   passenger_id: 1,
 *   category: 'general',
 *   seat: '1A',
 *   trip_slug: 'trip-slug'
 *  }
 * ```
 *
 * @returns {Array} - Returns an array of tickets for each selected seat.
 *
 */
const getTicketsForTripWithConnections = ({
  seats,
  trips,
  passengers,
  isOpenTicket,
  justAdultOpenTicket,
}) => {
  const tickets = [];
  const cleanedWaySeats = seats.filter((seat) => !seat.isPickedAsAdjacent);
  // generate tickets for each passenger, each passenger can have multiple tickets for each trip
  // so we need to iterate over the passengers and the trips
  passengers.forEach((passenger, passengerIndex) => {
    trips.forEach((trip) => {
      const tripSeats = cleanedWaySeats.filter((seat) => seat.tripSlug === trip.trip.id);
      const passengerTripSeat = tripSeats[passengerIndex];
      const category = justAdultOpenTicket && isOpenTicket ? 'general' : passenger.busCategory;
      const ticket = {
        passenger_id: passenger.id,
        category,
        trip_slug: trip.trip.id,
        seat: passengerTripSeat.number,
        ...(passengerTripSeat?.seatFareDescription && {
          seat_fare_description: passengerTripSeat.seatFareDescription,
        }),
        ...(passengerTripSeat?.seatFareId && {
          seat_fare_id: passengerTripSeat.seatFareId,
        }),
        ...(passengerTripSeat?.dynamicPrice && {
          dynamic_price: passengerTripSeat.dynamicPrice,
        }),
        ...(passengerTripSeat?.dynamicPriceDollar && {
          dynamic_price_dollar: passengerTripSeat.dynamicPriceDollar,
        }),
      };
      tickets.push(ticket);
    });
  });
  return tickets;
};

/**
 * Serializes seats data for locking
 * @param {Array} trips - Array of trips
 * @param {Array} passengers - Array of passengers
 * @param {Array} seats - Array of seats
 * @param {string} way - Trip direction ('OUTBOUND' or 'INBOUND')
 * @returns {Array} - Serialized seats data
 */
function serializeLockSeats(trips, passengers, seats = [], way) {
  const { purchase, whitelabelConfig } = store.getState();
  const { returns, departs } = purchase.toJS();
  const { JUST_ADULT_OPEN_TICKET } = whitelabelConfig.features;

  const busPassengers = passengers.filter((p) => p.category !== 'infant');
  const flightPassengers = passengers;
  const isOpenTicket = way === 'departure' ? wayIsOpenTicket(departs) : wayIsOpenTicket(returns);

  let busTickets = [];
  const wayObject = way === 'departure' ? departs : returns;
  if (wayObject.hasConnections) {
    /**
     * @todo implement adjacent seats for purchase with connections, find a way to unify the logic
     */
    busTickets = getTicketsForTripWithConnections({
      seats,
      trips,
      passengers: busPassengers,
      isOpenTicket,
      justAdultOpenTicket: JUST_ADULT_OPEN_TICKET,
    });
  } else {
    // TODO: Implementar una estrategia con la cual dependiendo de si se tiene asientos contiguos o no, se utilice map o reduce dependiendo del caso
    busTickets = busPassengers.reduce((acc, passenger, index) => {
      const seatsWithoutAdjacent = seats.filter((seat) => !seat.isPickedAsAdjacent);
      const seat = seatsWithoutAdjacent[index];
      let category = passenger.busCategory;
      if (
        (JUST_ADULT_OPEN_TICKET && isOpenTicket) ||
        (passenger.busCategory === 'oversold' && Boolean(seat.number))
      ) {
        category = 'general';
      }
      const newTicket = {
        passenger_id: passenger.id,
        category,
        ...(seat && { seat: seat.number }),
        ...(seat?.seatLevel && { seat_level: seat.seatLevel }),
        ...(seat?.seatFloor && { seat_floor: seat.seatFloor }),
        ...(seat?.seatFareId && { seat_fare_id: seat.seatFareId }),
        ...(seat?.seatFareDescription && { seat_fare_description: seat.seatFareDescription }),
        // Nullish (not truthy) checks so present dynamic quotes like 0 or "0.00" survive.
        ...(seat?.dynamicPrice != null && { dynamic_price: seat.dynamicPrice }),
        ...(seat?.dynamicPriceDollar != null && { dynamic_price_dollar: seat.dynamicPriceDollar }),
      };

      if (seat && seat.isAdjacentPicked) {
        const adjacentSeatNumber = seat.adjacentSeats.numbers[0];
        newTicket.adjacent_seat = adjacentSeatNumber;
        const adjacentTicket = {
          passenger_id: passenger.id,
          seat: adjacentSeatNumber,
          category: 'general',
        };
        acc.push(newTicket, adjacentTicket);
      } else {
        acc.push(newTicket);
      }
      return acc;
    }, []);
  }

  const flightTickets = flightPassengers.map((passenger) => ({
    passenger_id: passenger.id,
    category: passenger.category,
  }));

  const [trip] = trips;
  if (trip.type === 'bus' || wayObject.hasConnections) {
    return {
      trip_slug: wayObject.hasConnections ? wayObject.tripSlug : trip.id,
      tickets: busTickets,
    };
  }
  return {
    trip_slug: trip.id,
    tickets: flightTickets,
  };
}

export function lockTicketsLegacy(
  way,
  token,
  trips,
  passengers,
  seats = [],
  { isCurrent = alwaysCurrent, ownership, attemptOwnership, onReservationOutcome } = {},
) {
  const busPassengers = passengers.filter((p) => p.category !== 'infant');
  const flightPassengers = passengers;

  const busTickets = busPassengers.map((passenger, index) => ({
    passenger_id: passenger.id,
    category: passenger.busCategory,
    ...(seats[index] && { seat: seats[index] }), // Only include seat if it exists
  }));

  const flightTickets = flightPassengers.map((passenger) => ({
    passenger_id: passenger.id,
    category: passenger.category,
  }));

  const payloads = trips.map((trip) => {
    if (trip.type === 'bus') {
      return { trip_slug: trip.id, tickets: busTickets };
    }

    return { trip_slug: trip.id, tickets: flightTickets };
  });

  return (dispatch) => {
    const ownsCurrent = getOwnershipPredicate(isCurrent, ownership, attemptOwnership);
    const superseded = () => {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return { superseded: true };
    };
    const loadingOwnerId = getLoadingOwnerId(ownership, attemptOwnership);
    if (!ownsCurrent()) return Promise.resolve(superseded());
    dispatch(requestTickets(way, loadingOwnerId));

    const requestPayloads = cloneReservationValue(payloads);
    let pollOutcomeSettled = false;
    const polls = pollingPromises({
      data: payloads,
      name: 'onReceiveTickets',
      create: (polling, payload) => purchaseApi.createTickets(token, payload, polling),
    });

    return Promise.all(polls)
      .then((polls) => {
        if (!ownsCurrent()) return superseded();
        polls.forEach(({ status }) => {
          checkIfRetriesExceeded(status);
        });

        const segments = requestPayloads.map((payload, index) => ({
          way,
          requestTripSlug: payload.trip_slug,
          status: polls[index]?.status,
          requestTickets: payload.tickets,
          receiptEnvelope: polls[index],
          receiptContainer: polls[index]?.payload,
          receiptSegment: polls[index]?.payload,
          tickets: polls[index]?.payload?.tickets || [],
        }));
        pollOutcomeSettled = true;
        safelyObserveReservationOutcome(onReservationOutcome, {
          successful: segments.every(({ status }) => status === 'finished'),
          status: segments.every(({ status }) => status === 'finished') ? 'finished' : 'failed',
          mode: 'legacy',
          token,
          segments,
        });
        if (!ownsCurrent()) return superseded();

        const tickets = polls.map(({ payload }) => payload.tickets);

        dispatch(receiveTickets(way, tickets, null, loadingOwnerId));
        if (!ownsCurrent()) return superseded();
        return dispatch(
          getPurchase(token, true, { isCurrent: ownsCurrent, ownership, attemptOwnership }),
        );
      })
      .catch((reason) => {
        if (!ownsCurrent()) return superseded();
        if (!pollOutcomeSettled) {
          pollOutcomeSettled = true;
          safelyObserveReservationOutcome(onReservationOutcome, {
            successful: false,
            status: reason?.status || 'failed',
            mode: 'legacy',
            token,
            segments: requestPayloads.map((payload) => ({
              way,
              requestTripSlug: payload.trip_slug,
              status: reason?.status || 'failed',
              requestTickets: payload.tickets,
              receiptEnvelope: reason,
              receiptContainer: reason?.payload,
              receiptSegment: reason?.payload,
              tickets: reason?.payload?.tickets || [],
            })),
          });
        }
        const knownErrors = ['occupied'];
        if (knownErrors.includes(reason.type)) {
          dispatch(setError(204, reason.message, 'warning', false));
          dispatch(receiveTickets(way, [], null, loadingOwnerId));
        } else {
          dispatch(setError(204, 'Ocurrió un error al reservar tus asientos'));
        }
        dispatch(releaseQuickPurchaseLoading(loadingOwnerId));
        trackLockSeatsError({ code: reason?.type || reason?.code, message: reason?.message });
        console.error(reason);
      });
  };
}

/**
 * Function that handles the error when polling tickets
 * @param {Object} reason - Error object
 * @returns dispatch
 */
function lockTicketsPollingError(
  reason,
  { isCurrent = alwaysCurrent, ownership, attemptOwnership, allowOpaqueBfoError = false } = {},
) {
  return (dispatch) => {
    const ownsCurrent = getOwnershipPredicate(isCurrent, ownership, attemptOwnership);
    if (!ownsCurrent()) {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return;
    }
    const loadingOwnerId = getLoadingOwnerId(ownership, attemptOwnership);
    trackLockSeatsError({ code: reason?.payload?.errors?.state?.[0], message: reason?.message });
    const { purchase, whitelabelConfig } = store.getState();
    const plainPurchase = purchase.toJS();
    const { isExchange } = plainPurchase;
    const { payload } = reason;

    // RM-1201: a stale BFO quote uses the narrow fail-closed reselection recovery. The
    // typed poll error is trusted inside supported journeys; the opaque initial code-100
    // rejection is treated as BFO only when the active purchase is canonical BFO.
    if (
      isBfoSupportedJourney({
        purchase: plainPurchase,
        features: whitelabelConfig.features,
        brand: whitelabelConfig.env?.brand,
      }) &&
      isBfoReturnedPurchase(plainPurchase)
    ) {
      const typedBfoError = isBfoRefreshRequiredPollError(reason);
      const guardedOpaqueError =
        allowOpaqueBfoError &&
        !plainPurchase.bfoOpaqueRecoveryAttempted &&
        isBfoOpaqueInitialLockError(reason);
      if (typedBfoError || guardedOpaqueError) {
        if (guardedOpaqueError) dispatch(bfoOpaqueRecoveryAttempted());
        dispatch(recoverFromBfoPriceRefresh());
        return;
      }
    }

    // A blocked edit is a 422 on the initial ticket_requests POST, so it arrives as
    // { errors: [{ code, message }] } (no `payload`). Backend already localizes the message for
    // every code in this shape (e.g. 60 = store payment reference exists, 61 = duplicate
    // intermunicipal free pass), so surface it as-is regardless of the specific code instead of
    // falling back to the generic "trip no longer available" / "try to change your seats".
    const [blockedError] = Array.isArray(reason?.errors) ? reason.errors : [];
    if (blockedError) {
      dispatch(setError(204, 'try_to_change_your_seats', 'error', false, blockedError.message));
      dispatch(receiveTickets('departs', [], blockedError.message, loadingOwnerId));
      dispatch(receiveTickets('returns', [], blockedError.message, loadingOwnerId));
      return dispatch(refreshBus({ isCurrent: ownsCurrent, ownership, attemptOwnership }));
    }

    if (!payload) {
      dispatch(setError(204, 'trip_no_longer_available', 'warning', true));
      dispatch(releaseQuickPurchaseLoading(loadingOwnerId));
      return;
    }

    const { errors } = payload;
    const { state } = errors;
    const [error] = state;

    if (/occupied/g.test(error)) {
      const message = error.split(':')[1] || 'try_to_change_your_seats';
      dispatch(setError(204, message, 'error', false));
    } else if (/unavailable_wallet/g.test(error)) {
      const message = error.split(':')[1] || 'try_to_change_your_seats';
      dispatch(setError(204, message, 'error', true, null, isExchange && EXCHANGE_HOME_PATH));
    } else if (/unavailable/g.test(error)) {
      dispatch(setError(204, 'discounts_not_available', 'warning', false));
    } else if (/exchange_limit_reached/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(
        setError(
          204,
          'try_to_change_your_seats',
          'error',
          true,
          message,
          isExchange && EXCHANGE_HOME_PATH,
        ),
      );
    } else if (/invalid_exchange/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(setError(204, 'exchange_cannot_be_used', 'warning', true, message));
    } else if (
      /exchange_time_exceed|exchange_after_departure|exchange_enable_time_exceeded/g.test(error)
    ) {
      const message = error.split(':')[1];
      const exchangeRedirectPath = isExchange ? getExchangeLockScheduleRedirectPath() : null;
      dispatch(
        setError(
          204,
          'exchange_cannot_be_used',
          'error',
          Boolean(exchangeRedirectPath),
          message,
          exchangeRedirectPath || undefined,
        ),
      );
    } else if (/exchange_in_progress/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(setError(204, 'exchange_in_progress', 'warning', true, message));
    } else if (/older_category_limit_reached/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(setError(204, message, 'error', false));
    } else if (/lap_child_gratuity_lock_failed/g.test(error)) {
      const message = error.split(':').slice(1).join(':') || null;
      dispatch(setError(204, 'try_to_change_your_seats', 'error', false, message));
    } else {
      dispatch(setError(204, 'try_to_change_your_seats', 'error', false));
    }
    dispatch(receiveTickets('departs', [], error, loadingOwnerId));
    dispatch(receiveTickets('returns', [], error, loadingOwnerId));
    if (!/unavailable/g.test(error)) dispatch(clearSeats('departs'));
    if (!/unavailable/g.test(error)) dispatch(clearSeats('returns'));
    console.error(reason);
    return dispatch(refreshBus({ isCurrent: ownsCurrent, ownership, attemptOwnership }));
  };
}

export function lockTickets(
  token,
  { departs, returns },
  passengers,
  {
    preservePaymentOptionSelection = false,
    isCurrent = alwaysCurrent,
    ownership,
    attemptOwnership,
    onReservationOutcome,
  } = {},
) {
  return (dispatch, getState) => {
    const ownsCurrent = getOwnershipPredicate(isCurrent, ownership, attemptOwnership);
    const superseded = () => {
      releaseOwnedQuickPurchaseLoading(dispatch, ownership, attemptOwnership);
      return { superseded: true };
    };
    const loadingOwnerId = getLoadingOwnerId(ownership, attemptOwnership);
    if (!ownsCurrent()) return Promise.resolve(superseded());
    const purchase = getState().purchase.toJS();
    const payloads = {};
    const departureSeats = departs[0].selectedSeats;

    if (purchase.departs.hasConnections) {
      payloads.departs = serializeLockSeats(
        purchase.departs.trips || [],
        passengers,
        departureSeats,
        'departure',
      );
    } else {
      payloads.departs = serializeLockSeats(departs, passengers, departureSeats, 'departure');
    }

    if (returns) {
      const returnSeats = returns[0].selectedSeats;
      if (purchase.returns?.hasConnections) {
        payloads.returns = serializeLockSeats(
          purchase.returns.trips || [],
          passengers,
          returnSeats,
          'returns',
        );
      } else {
        payloads.returns = serializeLockSeats(returns, passengers, returnSeats, 'returns');
      }
    }

    if (!ownsCurrent()) return Promise.resolve(superseded());
    dispatch(requestTickets('departs', loadingOwnerId));
    if (returns) dispatch(requestTickets('returns', loadingOwnerId));

    const requestPayloads = cloneReservationValue(payloads);
    let pollOutcomeSettled = false;
    const polls = pollingPromises({
      data: [payloads],
      name: 'onReceiveTickets',
      create: (polling, payload) => purchaseApi.createTickets(token, payload, polling),
    });

    return Promise.all(polls)
      .then((polls) => {
        if (!ownsCurrent()) return superseded();
        polls.forEach(({ status }) => {
          checkIfRetriesExceeded(status);
        });

        const [poll] = polls;
        const ways = ['departs', 'returns'].filter((way) => requestPayloads[way]);
        const segments = ways.map((way) => ({
          way,
          requestTripSlug: requestPayloads[way].trip_slug,
          status: poll?.status,
          requestTickets: requestPayloads[way].tickets,
          receiptEnvelope: poll,
          receiptContainer: poll?.payload,
          receiptSegment: poll?.payload?.[way],
          tickets: poll?.payload?.[way]?.tickets || [],
        }));
        pollOutcomeSettled = true;
        safelyObserveReservationOutcome(onReservationOutcome, {
          successful: poll?.status === 'finished',
          status: poll?.status,
          mode: 'modern',
          token,
          segments,
        });
        if (!ownsCurrent()) return superseded();

        const departsTickets = polls.map(({ payload }) => payload.departs.tickets);
        dispatch(receiveTickets('departs', departsTickets, null, loadingOwnerId));

        if (returns) {
          const returnsTickets = polls.map(({ payload }) => payload.returns.tickets);
          dispatch(receiveTickets('returns', returnsTickets, null, loadingOwnerId));
        }
        if (!ownsCurrent()) return superseded();
        return dispatch(
          getPurchase(token, true, {
            preservePaymentOptionSelection,
            isCurrent: ownsCurrent,
            ownership,
            attemptOwnership,
          }),
        );
      })
      .catch((reason) => {
        if (!ownsCurrent()) return superseded();
        if (!pollOutcomeSettled) {
          pollOutcomeSettled = true;
          safelyObserveReservationOutcome(onReservationOutcome, {
            successful: false,
            status: reason?.status || 'failed',
            mode: 'modern',
            token,
            segments: Object.keys(requestPayloads).map((way) => ({
              way,
              requestTripSlug: requestPayloads[way].trip_slug,
              status: reason?.status || 'failed',
              requestTickets: requestPayloads[way].tickets,
              receiptEnvelope: reason,
              receiptContainer: reason?.payload,
              receiptSegment: reason?.payload?.[way],
              tickets: reason?.payload?.[way]?.tickets || [],
            })),
          });
        }
        return dispatch(
          lockTicketsPollingError(reason, {
            isCurrent: ownsCurrent,
            ownership,
            attemptOwnership,
            allowOpaqueBfoError: true,
          }),
        );
      });
  };
}

export function clearTicketsErrorType() {
  return {
    type: types.CLEAR_TICKETS_ERROR_TYPE,
  };
}

/**
 * Function that removes the file size segment from the document
 *
 * @param {string} document - The document with the file size segment
 * @returns {string} The document without the file size segment
 */
export function documentWithoutSize(document) {
  const originalDataSegment = document.split('::')[1];
  return originalDataSegment;
}

export function savePassengersWithTickets(token, fields, trips) {
  return (dispatch, getState) => {
    const state = getState();
    const { env, features } = selectWhitelabelConfig(state);
    const purchase = selectPurchase(state);
    const normalizedFields = { ...fields };
    const phoneCodeFallback = fields.phoneCode ?? null;
    normalizedFields.phoneCode = phoneCodeFallback;
    const normalizedPhoneCountry = fields.phoneCountry?.trim()?.toUpperCase();

    if (normalizedPhoneCountry) {
      normalizedFields.phoneCountry = normalizedPhoneCountry;
      try {
        normalizedFields.phoneCode = getCountryCallingCode(normalizedPhoneCountry);
      } catch (error) {
        normalizedFields.phoneCode = phoneCodeFallback;
      }
    }

    const purchasePayloadTypes = {
      gfa: decamelizeKeys({
        purchaserNationalityId: 1,
        ...omit(normalizedFields, 'passengers'),
        email: normalizedFields.email,
      }),
      default: decamelizeKeys({
        purchaserNationalityId: 1,
        omitEmailValidation: true, // This is used to omit the email validation for the purchaser so doesn't affect the passenger email validation
        ...omit(normalizedFields, 'passengers'),
        email: features.NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD
          ? normalizedFields.passengers[0].email
          : normalizedFields.email,
      }),
    };
    const { availableWallets = [] } = purchase;
    const purchasePayloadBase = purchasePayloadTypes[env.brand] ?? purchasePayloadTypes.default;

    // Get selected products from cart state to include ancillaries with passengers
    const selectedProducts = selectSelectedProducts(state);
    const ancillariesByPassenger = transformProductsToAncillaries(selectedProducts);

    const passengersPayload = decamelizeKeys(
      fields.passengers.map((passenger, index) => {
        const passengerAncillaries = ancillariesByPassenger[index] || {
          outgoing_ancillaries: [],
          incoming_ancillaries: [],
        };

        return {
          ...formatName(stripUiOnlyPassengerMeta(passenger)),
          ...(features.DOCUMENT_PER_PASSENGER && {
            document: documentWithoutSize(passenger?.document),
          }),
          nationalityId: purchasePayloadBase.purchaser_nationality_id,
          document_type: getProcessorDocumentType(passenger.documentType),
          isoCountryCode: passenger.nationality,
          phone: normalizeRegex('[^0-9]', passenger.phone),
          ...passengerAncillaries,
        };
      }),
    );

    dispatch(postPassengers());

    // The user token is set in the requests
    setUserTokenForAPI();
    // Getting the wallet type to update the purchase
    const walletType = getWalletTypeForPurchase();
    const isValidWalletType = walletType && availableWallets?.includes(walletType);

    const purchasePayload = {
      ...purchasePayloadBase,
      wallet_type: isValidWalletType ? walletType : null,
      used_frequent_passengers: fields.passengers.some(
        (passenger) => passenger.isFrequentPassenger,
      ),
      used_autoselected_passenger: fields.passengers.some(
        (passenger) => passenger.preferredPassengerOrder,
      ),
    };

    return purchaseApi
      .update(token, purchasePayload)
      .then((purchaseResponse) => {
        if (purchaseResponse.purchase.passengers.length) {
          return purchaseApi.updatePassengers(token, passengersPayload);
        }

        passengersPayload.forEach((passenger) => {
          Reflect.deleteProperty(passenger, 'id');
        });
        return purchaseApi.createPassengers(token, passengersPayload);
      })
      .then((payload) => {
        const { departs, returns } = trips;
        const { passengers } = camelizeKeys(payload);
        const { features } = getState().whitelabelConfig;
        const departureSeats =
          departs[0].selectedSeats && departs[0].selectedSeats.map(({ number }) => number);

        if (features.USE_LEGACY_LOCK_TICKETS) {
          dispatch(lockTicketsLegacy('departs', token, departs, passengers, departureSeats));

          if (returns) {
            const returnSeats =
              returns[0].selectedSeats && returns[0].selectedSeats.map(({ number }) => number);
            dispatch(lockTicketsLegacy('returns', token, returns, passengers, returnSeats));
          }
        } else {
          dispatch(lockTickets(token, { departs, returns }, passengers));
        }
        dispatch(setPassengers(passengers, token));
      })
      .catch((reason) => {
        dispatch(setPassengers([], token));

        // Check if this is a product unavailability error
        if (isProductUnavailableError(reason)) {
          const productCodes = extractUnavailableProductCodes(reason);
          if (productCodes.length > 0) {
            dispatch(handleProductUnavailable({ productCodes }));
            // Show user-friendly error message
            dispatch(setError(202, 'product_unavailable_error', 'warning', false));
          }
          return;
        }

        // Any passenger-field rule parseBackendErrors can place on a field — the e-mail rule and
        // the minor document-type rule today. Asked generically on purpose: gating this on one
        // rule's matcher is what left the minor document-type mapping unreachable in production
        // while its unit tests passed.
        if (hasParseablePassengerFieldError(reason)) {
          handleBackendFieldError({
            reason,
            dispatch,
            formName: 'passengers',
            errorCode: 202,
          });
          return;
        }

        // Fallback to general error handling — surface the backend message verbatim if present.
        //
        // It has to be read off the response, not off `reason.message`: js-api-client's
        // errorTypes.js remaps the 422's code 3 (INCORRECT_PARAMS) onto a hardcoded
        // 'Parámetros incorrectos', discarding what platform-purchase actually said. With no
        // customMsg the banner then renders `t('error.Parámetros incorrectos')`, which has no
        // translation key, so the buyer was shown the literal string 'error.Parámetros incorrectos'.
        //
        // This is the only channel the set-level rules have. `minor_traveling_alone` fails the
        // submitted set rather than one passenger, so it carries no index and no field rule above can
        // place it — see task 6.2 of validate-minor-policy-and-discounts.
        const errorMessage = backendBannerMessage(reason);
        dispatch(setError(202, reason.message, 'error', false, errorMessage));
        console.error(reason);
      });
  };
}

/**
 * Saves multiple passengers for bulk ticket purchases
 * @param {string} token - Purchase token
 * @param {Array<Object>} passengerPayload - Array of passenger objects containing passenger details
 * @returns {Function} Redux thunk action
 */
export function saveBulkPassengers(token, passengerPayload) {
  return (dispatch, getState) => {
    // Follows the same logic as savePassengersWithTickets but for bulk tickets
    // Considerate: bulk tickets have no seats and no returns
    dispatch(updatePurchase(true));
    const { env, features } = getState().whitelabelConfig;
    const purchasePayloadTypes = {
      gfa: decamelizeKeys({
        purchaserNationalityId: 1,
        email: passengerPayload[0].email,
      }),
      conectagfa: decamelizeKeys({
        purchaserNationalityId: 1,
        email: passengerPayload[0].email,
      }),
      default: decamelizeKeys({
        purchaserNationalityId: 1,
        email: features.NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD ? passengerPayload[0].email : '',
      }),
    };
    const purchasePayload = purchasePayloadTypes[env.brand] ?? purchasePayloadTypes.default;

    const passengersPayload = decamelizeKeys(
      passengerPayload.map((passenger) => ({
        ...formatName(stripUiOnlyPassengerMeta(passenger)),
        ...(features.DOCUMENT_PER_PASSENGER && {
          document: documentWithoutSize(passenger?.document),
        }),
        nationalityId: purchasePayload.purchaser_nationality_id,
        document_type: getProcessorDocumentType(passenger.documentType),
        isoCountryCode: passenger.nationality,
        phone: normalizeRegex('[^0-9]', passenger.phone),
      })),
    );

    setUserTokenForAPI();

    return purchaseApi
      .update(token, purchasePayload)
      .then((purchaseResponse) => {
        if (purchaseResponse.purchase.passengers.length) {
          return purchaseApi.updatePassengers(token, passengersPayload);
        }

        passengersPayload.forEach((passenger) => {
          Reflect.deleteProperty(passenger, 'id');
        });
        return purchaseApi.createPassengers(token, passengersPayload);
      })
      .then((payload) => {
        const { passengers } = camelizeKeys(payload);
        const { departs, returns, roundTrip } = store.getState().purchase.toJS();

        // create payloads for tickets without seats
        const payloads = {
          departs: {
            trip_slug: departs.tripSlug || departs.fragments[0].id,
            tickets: passengers.map((passenger) => ({
              passenger_id: passenger.id,
              category: passenger.busCategory || 'general',
            })),
          },
          returns: roundTrip
            ? {
                trip_slug: returns.tripSlug || returns.fragments[0].id,
                tickets: passengers.map((passenger) => ({
                  passenger_id: passenger.id,
                  category: passenger.busCategory || 'general',
                })),
              }
            : undefined,
        };

        dispatch(requestTickets('departs'));

        const polls = pollingPromises({
          data: [payloads],
          name: 'onReceiveTickets',
          create: (polling, payload) => purchaseApi.createTickets(token, payload, polling),
        });

        return Promise.all(polls)
          .then((polls) => {
            polls.forEach(({ status }) => {
              checkIfRetriesExceeded(status);
            });

            const departsTickets = polls.map(({ payload }) => payload.departs.tickets);
            dispatch(receiveTickets('departs', departsTickets));
            // Keep the method the buyer picked (e.g. Aplazo on a bulk purchase that cleared
            // the minimum) instead of resetting to the tenant default: bnplPaymentAttempt
            // reads selectedPaymentMethod fresh from the store when charging, so a reset here
            // would charge the default method behind the buyer's back.
            dispatch(getPurchase(token, true, { preservePaymentOptionSelection: true }));
            dispatch(setPassengers(passengers, token));
            // Disable isUpdating after successful completion
            dispatch({ type: types.UPDATE_PURCHASE, updating: false });
          })
          .catch((reason) => {
            dispatch(setError(202, reason.message, 'error', false));
            // Disable isUpdating in case of error
            dispatch({ type: types.UPDATE_PURCHASE, updating: false });
            console.error(reason);
          });
      })
      .catch((reason) => {
        dispatch(setError(202, reason.message, 'error', false));
        // Disable isUpdating in case of error
        dispatch({ type: types.UPDATE_PURCHASE, updating: false });
        console.error(reason);
      });
  };
}

export function fetchingTaxpayerId(updating) {
  return {
    type: types.FETCHING_TAXPAYER_ID,
    updating,
  };
}

/**
 * Validates the Taxpayer ID for Cruz del Sur
 *
 * @param {string} taxpayerId - The taxpayer ID to validate
 * @returns {Function} A thunk action creator
 */
export function validateTaxpayerId(taxpayerId) {
  return (dispatch, getState) => {
    if (taxpayerId.length < 11) {
      dispatch(changeFormField('purchaser', 'taxpayerLegalName', ''));
      dispatch(changeFormField('purchaser', 'taxpayerAddress', ''));
      return;
    }
    const state = getState();
    const purchaseToken = state.purchase.toJS().token;
    const params = { taxpayerId, purchaseToken };

    // PRO2-128: the response can land after the buyer has moved on — retyped the id, or turned the
    // invoice toggle off. Writing then re-arms a sentinel the toggle-off cleanup just removed, and
    // that cleanup will not fire again: it is keyed on `taxpayerId` still being set, which it no
    // longer is. The buyer ends up blocked by a read-only field with no way back. So every write
    // below is gated on the form still waiting for THIS lookup.
    const isStale = () => {
      const values = getReduxFormValues('purchaser')(getState()) || {};
      return !values.invoice || values.taxpayerId !== taxpayerId;
    };

    // The `.finally` below has always cleared this flag, but nothing ever set it, so the in-flight
    // guard it drives (`TaxpayerIdInput`'s disabled input, `Checkout.js`) was permanently off.
    dispatch(fetchingTaxpayerId(true));
    return purchaseApi
      .validateTaxPayer(params)
      .then((taxPayerResponse) => {
        if (isStale()) return;
        const camelizedTaxPayerResponse = camelizeKeys(taxPayerResponse);
        const { taxpayerLegalName, taxpayerAddress } = camelizedTaxPayerResponse.body;

        if (!taxpayerLegalName || taxpayerLegalName === 'unknown or empty RUC parameter') {
          dispatch(changeFormField('purchaser', 'taxpayerLegalName', 'RUC Invalido'));
          dispatch(changeFormField('purchaser', 'taxpayerAddress', ''));
          throw new Error('Invalid RUC');
        }

        dispatch(changeFormField('purchaser', 'taxpayerLegalName', taxpayerLegalName));
        dispatch(changeFormField('purchaser', 'taxpayerAddress', taxpayerAddress || ''));
      })
      .catch((error) => {
        if (isStale()) return;
        dispatch(changeFormField('purchaser', 'taxpayerLegalName', 'RUC Invalido'));
        throw new Error('Invalid RUC', error);
      })
      .finally(() => {
        dispatch(fetchingTaxpayerId(false));
      });
  };
}

/**
 * Function to validate the change of document
 * @param {Object} params - The parameters for the change document validation
 * @param {string} params.taxpayerId - The taxpayer ID to validate
 * @param {string} params.ticket - The ticket to validate
 * @returns {Function} A thunk action creator that returns the validation response
 */
export function validateChangeDocument(params) {
  return (dispatch) => {
    dispatch({ type: types.VALIDATE_CHANGE_DOCUMENT_LOADING, isLoading: true });
    return purchaseApi
      .validateChangeDocument(params)
      .then((response) => {
        dispatch({ type: types.VALIDATE_CHANGE_DOCUMENT_RECEIVE, data: response });
        return response;
      })
      .catch((error) => {
        const errorResponse = { success: false, message: error.message || 'Validation failed' };
        return errorResponse;
      })
      .finally(() => {
        dispatch({ type: types.VALIDATE_CHANGE_DOCUMENT_LOADING, isLoading: false });
      });
  };
}

/**
 * Function to change the document
 * @param {Object} params - The parameters for the document change
 * @returns {Function} A thunk action creator that returns the change document response
 */
export function changeDocument(params) {
  return (dispatch) => {
    dispatch({ type: types.CHANGE_DOCUMENT_LOADING, isLoading: true });

    return purchaseApi
      .changeDocument(params)
      .then((response) => {
        dispatch({ type: types.CHANGE_DOCUMENT_RECEIVE, data: response });
        return response;
      })
      .catch((error) => {
        const errorResponse = {
          success: false,
          message: error.message || 'Change document failed',
        };
        return errorResponse;
      })
      .finally(() => {
        dispatch({ type: types.CHANGE_DOCUMENT_LOADING, isLoading: false });
      });
  };
}

/**
 * Fetches and populates client data based on the provided document type and ID.
 *
 * @param {string} formName - The name of the form to update with client data.
 * @param {string} documentType - The type of the document (e.g., passport, ID).
 * @param {string} documentNumber - The document number used for fetching client data.
 * @returns {Function} A thunk action creator that dispatches form field updates.
 */
export function fetchClientDataByDocument(formName, documentType, documentNumber) {
  return (dispatch, getState) => {
    if (!documentNumber) return;

    const state = getState();
    const purchaseToken = state.purchase.get('token');

    const params = {
      documentType,
      documentNumber,
      purchaseToken,
    };

    return purchaseApi
      .autocompleteByDNIDocument(params)
      .then((documentResponse) => {
        const camelizedDocumentResponse = camelizeKeys(documentResponse);

        const { birthDate, name, lastName, secondLastName, nationality } =
          camelizedDocumentResponse;
        const [day, month, year] = birthDate.split('/');
        const formattedBirthDate = `${year}-${month}-${day}`;
        dispatch(changeFormField('passengers', `${formName}.firstName`, name));
        dispatch(changeFormField('passengers', `${formName}.lastName`, lastName));
        dispatch(changeFormField('passengers', `${formName}.secondLastName`, secondLastName));
        dispatch(changeFormField('passengers', `${formName}.dateOfBirth`, formattedBirthDate));
        dispatch(changeFormField('passengers', `${formName}.nationality`, nationality));
      })
      .catch((error) => console.error('Error fetching client data: ', error));
  };
}

export function toggleInsurance(updating) {
  return { type: types.TOGGLE_INSURANCE, updating };
}

export function toggleWantsInsurance(token, wants) {
  return (dispatch) => {
    dispatch(toggleInsurance(true));

    return purchaseApi
      .update(token, { wants_insurance: wants })
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleInsurance(false));
      });
  };
}

export function toggleUsingWallet(updating) {
  return { type: types.UPDATE_USING_WALLET, updating };
}

export function requestDiscount() {
  return { type: types.REQUEST_DISCOUNT };
}

export function receiveDiscount() {
  return { type: types.RECEIVE_DISCOUNT };
}

export function setUsingWallet(token, useWallet, amount = null, updating) {
  return (dispatch) => {
    const { walletType } = store.getState().purchase.toJS();
    if (amount && amount <= 0) {
      dispatch(setError(205, 'error_when_using_points', 'error', false));
      return;
    }

    dispatch(toggleUsingWallet(true));

    // return a promise to wait for
    return purchaseApi
      .updateWallet(token, updating ? false : useWallet, { amount })
      .then((payload) => {
        if (updating) return dispatch(setUsingWallet(token, true, amount));

        const message = useWallet
          ? `buy_using_your_points_${walletType}`
          : `buy_without_using_your_points_${walletType}`;
        const messageType = useWallet ? 'success' : 'warning';

        dispatch(resetInstallmentsPlan());
        dispatch(changeFormField('card', 'paymentPlan', 'single'));
        dispatch(resetForm('installmentsSelector'));
        dispatch(receivePurchase(payload));
        dispatch(setError(null, message, messageType, false));
      })
      .catch((reason) => {
        if (reason.code === 'INVALID_USER') {
          dispatch(setError(205, 'user_is_not_a_passenger', 'warning', false));
        } else {
          dispatch(setError(205, 'error_when_using_points', 'error', false));
        }
        console.error(reason);
      })
      .finally(() => dispatch(toggleUsingWallet(false)));
  };
}

// The codes that actually reach this catch are the ones js-api-client hands over, not the ones
// platform-purchase sent: its errorTypes table rewrites INVALID_DISCOUNT 11 -> 500 and
// EXPIRED_DISCOUNT 12 -> 501 first, and a real HTTP 500 is thrown as NetworkError(101), never 500.
//
// 500 here is therefore AMBIGUOUS, and the map is right about it only by luck. It is both
// INVALID_DISCOUNT 11 after that rewrite AND `ApiErrors::INTERNAL_SERVER_ERROR = 500` arriving
// untouched, because the table is keyed on backend codes and has no entry for 500 to rewrite.
// `Discounts::RemoteApplier` reports every provider rejection that way, so on a remote-coupon
// tenant a mistyped code lands here as 500 too. Both deserve `invalid_discount`, which is why the
// key is correct either way — but the accompanying prose is not interchangeable, and the catch
// below keys that on the raw code instead.
//
// The raw backend codes stay as aliases in case that table is ever flattened;
// INVALID_ROUTE_DISCOUNT (14) is passed through untouched today.
const DISCOUNT_ERROR_KEYS = {
  500: 'invalid_discount',
  501: 'expired_discount',
  14: 'invalid_route_discount',
  11: 'invalid_discount',
  12: 'expired_discount',
};

/**
 * Picks the notification key for a rejected discount code.
 * @param {number} code - ApiErrors code carried by the rejection.
 * @returns {string} Notification key under the `error` namespace.
 */
function discountErrorKey(code) {
  return DISCOUNT_ERROR_KEYS[code] || 'error_when_applying_discount_code';
}

/** Refresh only the referral purchase after an uncertain coupon response. */
export function reconcileReferralDiscount(token, selectedInstallmentsPlan) {
  return (dispatch) =>
    purchaseApi.get(token).then((payload) => {
      if (store.getState().purchase?.get('token') !== token) return;

      const { qualifiesForMonthlyInstallments, paymentPlans } = camelizeKeys(payload.purchase);
      if (
        !isPlanAvailable(qualifiesForMonthlyInstallments, selectedInstallmentsPlan, paymentPlans)
      ) {
        dispatch(resetInstallmentsPlan());
        dispatch(changeFormField('card', 'paymentPlan', 'single'));
        dispatch(resetForm('installmentsSelector'));
      }
      dispatch(setPaymentPlans(paymentPlans));
      dispatch(receivePurchase(payload));
    });
}

export function applyDiscount(token, code, selectedInstallmentsPlan, { guardToken = false } = {}) {
  return (dispatch) => {
    dispatch(requestDiscount());
    const staleReferral = () => guardToken && store.getState().purchase?.get('token') !== token;

    return purchaseApi
      .applyDiscountCode(token, code, selectedInstallmentsPlan)
      .then((payload) => {
        if (staleReferral()) return;
        const { qualifiesForMonthlyInstallments, paymentPlans } = camelizeKeys(payload.purchase);
        const i18nKey = 'code_applied_to_purchase';

        // When a discount is applied, the backend returns updated paymentPlans with new amounts.
        // Check if the user's currently selected installment plan is still available.
        // If available, update it with the new post-discount amounts to keep UI in sync.
        // If not available, reset the installments selection.
        const currentPlanAvailable = isPlanAvailable(
          qualifiesForMonthlyInstallments,
          selectedInstallmentsPlan,
          paymentPlans,
        );

        if (currentPlanAvailable) {
          const { card, months } = selectedInstallmentsPlan;
          const planData = paymentPlans?.[card]?.[months];
          if (planData) {
            const total =
              planData.total || (planData.monthlyPayment ? months * planData.monthlyPayment : 0);
            payload.purchase.monthly_selected_plan = months;
            payload.purchase.selectedInstallmentsPlan = {
              ...selectedInstallmentsPlan,
              ...planData,
              total,
            };
          }
        } else {
          dispatch(resetInstallmentsPlan());
          dispatch(changeFormField('card', 'paymentPlan', 'single'));
          dispatch(resetForm('installmentsSelector'));
        }

        dispatch(setPaymentPlans(paymentPlans));
        dispatch(receivePurchase(payload));
        dispatch(setError(null, i18nKey, 'success', false));
      })
      .catch((reason) => {
        if (staleReferral()) return;
        dispatch(resetForm('discountCode'));
        // One refusal carries prose the funnel cannot reproduce: a remote coupon that does not
        // improve the price reads "será válido cuando sea mayor al descuento de tu compra en
        // línea", and `add_remote_error` shares INVALID_DISCOUNT with a genuinely wrong code, so
        // `reason.code` alone cannot separate them. Three conditions gate showing it.
        //
        // Structured, so a gateway or WAF error page is never shown verbatim: `details.message` is
        // synthesised by the API client from the first 200 characters of any non-JSON body.
        const hasStructuredErrors =
          Array.isArray(reason?.errors) || Array.isArray(reason?.details?.errors);
        // The RAW backend code, which survives in `errors[0]`. `reason.code` has already been
        // rewritten by the client and 500 is ambiguous there: it is both INVALID_DISCOUNT 11
        // remapped AND `ApiErrors::INTERNAL_SERVER_ERROR` passed through untouched. The second is
        // how `Discounts::RemoteApplier` reports any provider rejection — the normal path for a
        // mistyped code on CDS — and it carries the provider's own text, which no adapter
        // translates (saas-integrations throws `ServiceError('Invalid discount.')`). Worse, when
        // the provider sends nothing, purchase falls back to `api.v2.discount.internal_server_error`,
        // a key that exists in no locale. Only `BACKEND_INVALID_DISCOUNT` is worth reading; every
        // other code has a funnel string at least as good.
        const rawCode =
          reason?.details?.code ?? reason?.errors?.[0]?.code ?? reason?.details?.errors?.[0]?.code;
        // The backend does not localise these. `Api::V2::DiscountsController` includes no
        // `Localizable`, `DiscountParams` builds the text with a bare `I18n.t`, and `default_locale`
        // is es-MX with fallbacks on — pt-BR carries no discount keys at all. So the prose is
        // Spanish whatever the buyer's UI says, and showing it to a pt-BR or en-US buyer replaces a
        // correct translation with one they cannot read.
        const proseFitsUi = CurrentLang().startsWith('es');
        const backendMessage =
          hasStructuredErrors && rawCode === BACKEND_INVALID_DISCOUNT && proseFitsUi
            ? backendBannerMessage(reason)
            : null;
        dispatch(setError(206, discountErrorKey(reason?.code), 'error', false, backendMessage));
      })
      .finally(() => {
        if (!staleReferral()) dispatch(receiveDiscount());
      });
  };
}

export function formErrors(funnelStep, fields) {
  return {
    type: types.UA_FORM_ERRORS,
    funnelStep,
    fields,
  };
}

export function expirePurchase() {
  return {
    type: types.EXPIRE_PURCHASE,
  };
}

export function setPurchaseExpiration(expiration) {
  return {
    type: types.PURCHASE_SET_EXPIRATION,
    expiration,
  };
}

export function timeoutTick(milisecondsAdded) {
  return (dispatch, getState) => {
    const { purchase } = getState();

    const remainingTime = getExpirationTime(purchase.toJS(), milisecondsAdded);

    if (remainingTime <= 0) {
      dispatch(expirePurchase());
    }
  };
}

export function setEmailStatus(status) {
  return {
    type: types.SET_EMAIL_STATUS,
    status,
  };
}

export function sendEmail(token, email) {
  return (dispatch) => {
    dispatch(setEmailStatus('sending'));
    purchaseApi
      .sendEmail(token, email)
      .then(() => dispatch(setEmailStatus('sent')))
      .catch(() => dispatch(setEmailStatus('error')));
  };
}

/**
 * Update purchase total for bulk tickets
 * @param {*} passengers - Passengers
 * @returns - Dispatch action
 */
export function updateBulkPurchaseTotal(passengers) {
  const { purchase } = store.getState();
  const { departs, returns, roundTrip } = purchase.toJS();

  const departsPrices = departs.fragments[0].prices;
  const { total: departsTotal } = purchaseTotalByCategory(passengers, departsPrices);

  let total = departsTotal;

  // Add return total only if it's a round trip
  if (roundTrip) {
    const returnsPrices = returns.fragments[0].prices;
    const { total: returnsTotal } = purchaseTotalByCategory(passengers, returnsPrices);
    total += returnsTotal;
  }

  // Dispatch the action with the new total
  return {
    type: types.UPDATE_PURCHASE_TOTAL,
    total,
  };
}

function receivePurchaser(purchaser) {
  return {
    type: types.RECEIVE_PURCHASER,
    purchaser: {
      ...camelizeKeys(purchaser),
      // Agreement consumers compare the canonical backend meta keys. Preserve this map
      // verbatim so successful writes converge without renaming its consent or other keys.
      ...(purchaser.meta !== undefined && { meta: purchaser.meta }),
    },
  };
}

export function updatePaymentPlans(paymentPlans) {
  return {
    type: types.UPDATE_PAYMENT_PLANS,
    paymentPlans,
  };
}

/**
 * Action creator to toggle service levels loading state
 * @param {boolean} updating - Whether service levels are being updated
 * @returns {object} Action object
 */
function toggleServiceLevels(updating) {
  return { type: types.UPDATE_SERVICE_LEVEL, updating };
}

/**
 * Thunk action creator to toggle service levels for a purchase
 * @param {string} token - Purchase token
 * @param {string} serviceLevelId - Service level ID
 * @returns {Function} Thunk function
 */
export function toggleWantsServiceLevels(token, serviceLevelId) {
  const payload = { service_level_id: serviceLevelId };

  return (dispatch) => {
    dispatch(toggleServiceLevels(true));

    return purchaseApi
      .update(token, payload)
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleServiceLevels(false));
      });
  };
}

export function updatePurchaseFields(purchaseToken, fields) {
  return (dispatch) => {
    const {
      purchase,
      whitelabelConfig: {
        features,
        features: {
          NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD,
          IDENTIFICATION_DOCUMENT_FOR_PURCHASER,
          SHOW_PURCHASER_ADDRESS_FIELDS,
          SHOW_PROMOTIONAL_TERMS_CHECKBOX,
          BILLING_COMPANY_ENABLED,
          BILLING_COMPANY_AVAILABLE_DOCUMENTS,
        },
        env,
      },
      cdsFrequentPassenger,
      reserhubAccount,
    } = store.getState();
    const { passengers } = purchase.toJS();

    const showCdsFrequentPassengerOptIn = isCdsFrequentPassengerOptInEligible({
      features,
      brand: env.brand,
      cdsSession: {
        isBootstrapped: cdsFrequentPassenger?.get('isBootstrapped'),
        isAuthenticated: cdsFrequentPassenger?.get('isAuthenticated'),
        error: cdsFrequentPassenger?.get('error'),
        usesReserhubAccount: cdsFrequentPassenger?.get('usesReserhubAccount'),
      },
      reserhubUser: reserhubAccount?.get('user'),
      isReserhubUserLoading: reserhubAccount?.get('isUserLoading'),
      isReserhubUserFetched: reserhubAccount?.get('isUserFetched'),
      isReserhubUserAnonymous: reserhubAccount?.get('isUserAnonymous'),
    });

    const normalizedPhoneCountry = fields.phoneCountry?.trim()?.toUpperCase();
    let phoneCode;
    try {
      phoneCode = normalizedPhoneCountry
        ? getCountryCallingCode(normalizedPhoneCountry)
        : undefined;
    } catch (error) {
      phoneCode = undefined;
    }
    const phone =
      phoneCode && fields.phone && parsePhoneNumber(`+${phoneCode}${fields.phone}`)?.nationalNumber;
    const allowedBillingCompanyDocuments = getEnabledBillingCompanyDocuments(
      BILLING_COMPANY_ENABLED,
      BILLING_COMPANY_AVAILABLE_DOCUMENTS,
    );
    const billingCompanyFlowConfigured = allowedBillingCompanyDocuments.length > 0;
    // MID-555: invoicing a third party needs the buyer's address + city for crearTercero,
    // whatever the document type is. Only reachable with ENABLE_THIRD_PARTY_INVOICE on, so the
    // company condition below keeps deciding it for every other tenant. The legacy NIT case is
    // already covered by `isBillingCompanyDocumentOrLegacy` with an empty allowlist.
    const isThirdPartyBilling = isThirdPartyRecipient(fields.billingRecipientType);
    // A resolved company keeps the address the provider already holds: the form hides the section
    // and the payload drops it, so the buyer is never asked for data nobody consumes.
    const sendAddressAttributes =
      isThirdPartyBilling ||
      (isBillingCompanyDocumentOrLegacy(fields.documentType, allowedBillingCompanyDocuments) &&
        SHOW_PURCHASER_ADDRESS_FIELDS &&
        (!billingCompanyFlowConfigured || !fields.billingCompanyId));

    // The pre-existing invoice flow sends the raw form value inside the address; the company flow
    // sends the normalized ISO code. Kept apart so the legacy request shape stays byte-identical.
    const addressPhoneCountry = billingCompanyFlowConfigured
      ? normalizedPhoneCountry
      : fields.phoneCountry;
    // Checkbox markers never reach the API, and the phone/address keys are always rebuilt below,
    // so the passthrough spread drops all of them instead of relying on later keys to win.
    const scalarFields = omit(fields, [
      'promotionalTerms',
      'cdsFrequentPassengerOptIn',
      // PRO2-128: the invoice toggle decides the taxpayer block below but must not travel — the
      // backend's `params.permit` list has no `invoice`. Verified: that repo never sets
      // `action_on_unpermitted_parameters`, so production falls back to `false` — the key is
      // dropped silently, never logged and never raised. Stripping it is hygiene, not protection.
      'invoice',
      'billingCompanyId',
      'billingCompanyValidationEnabled',
      'phoneCountry',
      'phoneCode',
      'phone',
      'addressAttributes',
      // The city control is shared with the buyer address, so it holds a value even when the
      // provider already resolved the company, or when the document type cannot be billed at all.
      // Only a pending creation needs the city id. The UI marker is what the form already resolved
      // from the tenant allowlist, so this stays a single source of truth instead of a second read.
      ...(!billingCompanyFlowConfigured ||
      fields.billingCompanyId ||
      !fields.billingCompanyValidationEnabled
        ? ['billingCompanyCityId']
        : []),
    ]);
    const payload = decamelizeKeys({
      ...scalarFields,
      ...(normalizedPhoneCountry && { phoneCountry: normalizedPhoneCountry }),
      purchaserNationalityId: 1,
      documentType: IDENTIFICATION_DOCUMENT_FOR_PURCHASER ? fields.documentType : 'CC',
      ...(SHOW_PROMOTIONAL_TERMS_CHECKBOX && {
        acceptedPromotionalTerms: fields.promotionalTerms ? 'true' : 'false',
      }),
      phone,
      email: NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD ? passengers[0].email : fields.email,
      ...(normalizedPhoneCountry && { phoneCode }),
      // Taxpayer fields belong to the pre-existing invoice flow: only an explicit id fills the
      // block, and the address defaults so the request shape it has always sent is preserved.
      //
      // PRO2-128: an id alone cannot express a *withdrawal*. `redux-form` deletes a field cleared
      // to '' when it has no initial value (`createReducer` CHANGE), so once the buyer turns the
      // invoice toggle off, `taxpayerId` is gone from the form rather than empty — the spread below
      // then omits the block, and `purchase_update_params`' `meta.merge!(slice(...))` keeps the
      // taxpayer data already stored. The buyer would be invoiced against a RUC they revoked.
      // An explicit `invoice: false` therefore sends empty strings, which that same merge writes
      // over the stored values, and which `createReservePayloadRequest` maps back to `undefined`
      // so nothing reaches the provider.
      //
      // Deliberately narrow: this only fires when a caller passes `invoice === false`, so every
      // caller that does not send the marker keeps its exact current payload. It also stands down
      // for the billing-company flow, which owns `taxpayerLegalName`/`taxpayerAddress` for a
      // different purpose and must not have them cleared by an invoice toggle.
      //
      // Shared with `createPassengerSaveOnSubmit`, the other save path to this same purchase, which
      // does not come through here. Keeping one definition is the point — see that module.
      ...taxpayerPayloadFields(fields),
      // MID-555: invoice recipient the buyer chose (self / organization /
      // person). Forwarded to the provider that emits the invoice (FlotaOspina).
      // Guard on `!== undefined`, not truthiness: deselecting the recipient clears
      // the form value to '' and that clear MUST reach the backend (as null), or
      // it keeps the previous recipient and invoices the wrong party. `undefined`
      // (never set — every non-Fospina tenant) is still omitted, so their payload
      // is unchanged.
      ...(fields.billingRecipientType !== undefined && {
        billing_recipient_type: fields.billingRecipientType || null,
      }),
      addressAttributes: sendAddressAttributes
        ? {
            ...fields.addressAttributes,
            phoneCode,
            phoneCountry: addressPhoneCountry,
            phone,
          }
        : {},
      // Not run through decamelizeKeys' automatic name mapping: the backend meta key
      // (frequent_passenger_enrollment) intentionally differs from what cdsFrequentPassengerOptIn
      // would decamelize to (cds_frequent_passenger_opt_in).
      ...(showCdsFrequentPassengerOptIn && {
        frequent_passenger_enrollment: fields.cdsFrequentPassengerOptIn ? 'true' : 'false',
      }),
    });
    dispatch(updatePurchase(true));

    return purchaseApi
      .update(purchaseToken, payload)
      .then((response) => {
        /**
         * We retrieve only the fields that were updated in the payload
         * and update the purchase with them
         */
        const fieldsFromPayload = Object.keys(payload);
        const purchaser = response.purchase;
        const updatedFields = fieldsFromPayload.reduce((acc, field) => {
          acc[field] = purchaser[field];
          return acc;
        }, {});

        // billing_company_id is server-owned: accept the response value without ever sending the
        // form value back in the request.
        if (billingCompanyFlowConfigured) {
          const serverBillingCompanyId = purchaser.billing_company_id ?? purchaser.billingCompanyId;
          if (serverBillingCompanyId !== undefined) {
            updatedFields.billing_company_id = serverBillingCompanyId;
          }
        }

        if (
          payload.phone_country !== undefined &&
          (updatedFields.phone_country === undefined || updatedFields.phone_country === null)
        ) {
          updatedFields.phone_country = payload.phone_country;
        }

        // accepted_promotional_terms / frequent_passenger_enrollment are stored in meta by the
        // backend; assign form values so Redux has them and purchaserUpdated becomes true.
        // Use fresh purchase from store so we merge against latest meta instead of overwriting newer state
        if (SHOW_PROMOTIONAL_TERMS_CHECKBOX || showCdsFrequentPassengerOptIn) {
          const latestPurchase = store.getState().purchase;
          const currentMeta = latestPurchase.get('meta');
          const currentMetaJs =
            currentMeta && typeof currentMeta.toJS === 'function'
              ? currentMeta.toJS()
              : currentMeta || {};
          updatedFields.meta = {
            ...currentMetaJs,
            ...(SHOW_PROMOTIONAL_TERMS_CHECKBOX && {
              accepted_promotional_terms: fields.promotionalTerms ? 'true' : 'false',
            }),
            ...(showCdsFrequentPassengerOptIn && {
              frequent_passenger_enrollment: fields.cdsFrequentPassengerOptIn ? 'true' : 'false',
            }),
          };
        }

        dispatch(receivePurchaser(updatedFields));
      })
      .catch((reason) => {
        dispatch(updatePurchase(false));

        if (billingCompanyFlowConfigured && isBillingCompanyResolutionError(reason)) {
          companyCreationFailedTracker();
          dispatch(setError(204, 'billing_company_creation_failed', 'error', false));
          return Promise.reject(reason);
        }

        // Check if this is a purchaser email validation error
        if (isPurchaserEmailValidationError(reason)) {
          handleBackendFieldError({
            reason,
            dispatch,
            formName: 'purchaser',
            errorCode: 204,
          });
          return Promise.reject(reason);
        }

        // Fallback to general error handling
        dispatch(setError(204, reason.message, 'error', false));
        return Promise.reject(reason);
      });
  };
}

export function insurancesChecked() {
  return {
    type: types.PURCHASE_INSURANCES_CHECKED,
  };
}

export function updatePurchaseUser(purchaseToken) {
  return (dispatch) => {
    dispatch(updatePurchase(true));

    return purchaseApi
      .update(purchaseToken, {})
      .then((response) => {
        dispatch(receivePurchase(response));
      })
      .catch((reason) => {
        dispatch(updatePurchase(false));
        dispatch(setError(204, reason.message, 'error', false));
        return Promise.reject(reason);
      });
  };
}

/**
 * Updates the purchase with the accountId from the user account
 * @param {Object} params - The parameters for the update
 * @param {string|number} params.accountId - The user ID to set as accountId
 * @returns {Function} Thunk action
 */
export function updatePurchaseWithReserhubAccount({ accountId, isCurrent }) {
  return async (dispatch, getState) => {
    const { purchase } = getState();
    const plainState = purchase.toJS();
    const { token: purchaseToken, state, accountId: purchaseAccountId } = plainState;

    if (purchaseToken && purchaseAccountId !== accountId && state !== 'completed') {
      try {
        dispatch(updatePurchase(true));
        // Dummy update to change account id with auth token in requests
        const purchaseResponse = await purchaseApi.update(purchaseToken, {});

        // The session that asked for this may have been replaced while the request was in flight.
        // Publishing now would overwrite the attribution of whichever session owns the purchase.
        if (isCurrent && !isCurrent()) {
          dispatch(updatePurchase(false));
          return null;
        }

        dispatch(receivePurchase(purchaseResponse));
        return purchaseResponse;
      } catch (error) {
        dispatch(updatePurchase(false));
        return null;
      }
    }
  };
}

/**
 * Polls the purchase status and resolves or rejects based on the purchase state.
 *
 * @param {string} purchaseToken - The token representing the purchase.
 * @param {number} [minutes=1] - The duration in minutes to keep polling.
 * @param {number} [interval=2] - The interval in seconds between each poll.
 * @returns {Promise<Object>} - A promise that resolves with the purchase payload if the purchase is completed or canceled, or rejects with the poll payload if the purchase is not pending, completed, or canceled.
 */
export function purchaseStatusResult(purchaseToken, minutes = 1, interval = 2) {
  return new Promise((resolve, reject) => {
    const pollOptions = {
      interval: interval * 1000,
      maxRetries: (minutes * 60) / interval,
    };

    const onReceivePurchase = (poll) => {
      if (!['pending', 'completed', 'canceled'].includes(poll.status)) {
        return reject(poll);
      }

      const { state } = poll.payload.purchase;
      switch (state) {
        case 'completed':
          resolve(poll.payload);
          break;
        case 'canceled':
          resolve(poll.payload);
          break;
        case 'pending':
          break;
        case 'attempt':
          break;
        default:
          reject(poll.payload);
          break;
      }
    };

    purchaseApi.get(purchaseToken, null, {
      onReceivePurchase,
      options: pollOptions,
    });
  });
}

/**
 * Polls the purchase status until the purchase is complete.
 *
 * @param {string} purchasetoken - The token representing the purchase.
 * @param {boolean} isPixPayment - Flag indicating if the payment method is Pix.
 * @returns {Function} A thunk that dispatches the purchase status result.
 */
export function pollPurchaseComplete(purchasetoken, isPixPayment) {
  const defaultMinutes = 1;
  const pixMinutes = 27;
  const pollMinutes = isPixPayment ? pixMinutes : defaultMinutes;
  return (dispatch) => {
    purchaseStatusResult(purchasetoken, pollMinutes)
      .then((purchaseResponse) => {
        dispatch(receivePurchase(purchaseResponse));
      })
      .catch((e) => {
        console.log(e);
      });
  };
}

/**
 * Carbon ancillary actions
 */
export function toggleCarbonOffset(updating) {
  return { type: types.TOGGLE_CARBON_OFFSET, updating };
}

export function toggleWantsCarbonOffset(token, wants) {
  return (dispatch) => {
    dispatch(toggleCarbonOffset(true));

    return purchaseApi
      .update(token, { wants_carbon_offset: wants })
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleCarbonOffset(false));
      });
  };
}

/**
 * Set purchase expiration after extending
 * @param {*} extendedAt - Extended date
 * @param {*} expiresAt - New expiration date
 * @param {*} currentTime - Current time of the server
 * @returns dispatch
 */
export function setPurchaseExtendedExpiration(extendedAt, expiresAt, currentTime) {
  return {
    type: types.PURCHASE_EXTENDED_EXPIRATION,
    expiresAt,
    extendedAt,
    currentTime,
  };
}

/**
 * Request to extend purchase expiration
 * @param {String} token - Purchase token
 * @param {Function} errorFunction - function to execute if an error occures
 * @returns dispatch
 */
export function requestExtendPurchaseExpiration(token, errorFunction) {
  return (dispatch) => {
    dispatch(updatePurchase(true));
    purchaseApi
      .increaseExpiration(token)
      .then((purchase) => {
        if (!Object.keys(purchase).length) throw new Error();

        const {
          extendedAt,
          expiresAt,
          currentTime,
          incomingTicketRequestId,
          outgoingTicketRequestId,
          type,
        } = camelizeKeys(purchase);
        if (type !== 'ticket_request') {
          dispatch(setPurchaseExtendedExpiration(extendedAt, expiresAt, currentTime));
          dispatch(updatePurchase(false));
        } else {
          const dataToPoll = [{ id: outgoingTicketRequestId }];
          if (incomingTicketRequestId) dataToPoll.push({ id: incomingTicketRequestId });
          const polls = pollingPromises({
            data: dataToPoll,
            name: 'onReceiveTickets',
            create: (polling, payload) => purchaseApi.startTicketsPoll(token, payload, polling),
          });
          Promise.all(polls)
            .then((polls) => {
              polls.forEach(({ status }) => {
                checkIfRetriesExceeded(status);
              });
              dispatch(getPurchase(token));
            })
            .catch((reason) => {
              dispatch(lockTicketsPollingError(reason));
              dispatch(updatePurchase(false));
            });
        }
      })
      .catch(() => {
        dispatch(setError(205, 'error_when_extending_purchase', 'error', false));
        dispatch(updatePurchase(false));
        if (errorFunction) errorFunction();
      });
  };
}

/** Error handle of membership discount request */
function membershipDiscountError(errorCode, walletName, dispatch) {
  if (errorCode === 401 && (walletName === 'travelpass' || walletName === 'costapass')) {
    triggerSignOutWidget(walletName);
    dispatch(setCostaPassError(errorCode));
  } else {
    dispatch(setError(205, `error_when_using_wallet_${walletName}`, 'error', false));
  }
}

/** Method to apply a loyalty membership discount */
export function membershipDiscountPayment(token, discount, walletName) {
  return (dispatch) => {
    // The user token is set in the requests
    setUserTokenForAPI();
    dispatch(toggleUsingWallet(true));

    purchaseApi
      .membershipDiscountPayment(token, discount)
      .then(() => {
        dispatch(getPurchase(token));
        dispatch(setError(200, `buy_using_your_points_${walletName}`, 'success', false));
      })
      .catch((error) => {
        const { code } = error;
        membershipDiscountError(code, walletName, dispatch);
      })
      .finally(() => {
        dispatch(toggleUsingWallet(false));
      });
  };
}

/** Method to delete the membership discount applied */
export function membershipDiscountPaymentDelete(token, walletName) {
  return (dispatch) => {
    // The user token is set in the requests
    setUserTokenForAPI();
    dispatch(toggleUsingWallet(true));

    purchaseApi
      .membershipDiscountPaymentDelete(token)
      .then(() => {
        dispatch(setError(205, `buy_without_using_wallet_${walletName}`, 'warning', false));
        dispatch(getPurchase(token));
      })
      .catch((error) => {
        const { code } = error;
        membershipDiscountError(code, walletName, dispatch);
      })
      .finally(() => {
        dispatch(toggleUsingWallet(false));
      });
  };
}

/** Method to apply a loyalty membership discount
 * @param {Boolean} updating - boolean to indicate if the purchase is updating
 */
export function toggleFlatFare({ updating }) {
  return { type: types.TOGGLE_FLAT_FARE, updating };
}

/**
 * toggleWantsFlatFare action. Apply or remove a flat fare loyalty discount on a purchase.
 * @param {object} params - token, wantsFlatFare.
 * @param {string} params.token - Purchase token.
 * @param {boolean} params.wantsFlatFare - Whether the user wants the flat fare applied.
 */
export function toggleWantsFlatFare({ token, wantsFlatFare }) {
  return (dispatch) => {
    dispatch(toggleFlatFare({ updating: true }));

    return purchaseApi
      .update(token, { wants_flat_fare: wantsFlatFare })
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleFlatFare({ updating: false }));
      });
  };
}

/** Creates a Yuno checkout session
 * @param {string} purchaseUrl - Base URL for the purchase API
 * @param {string} purchaseToken - Token identifying the purchase
 * @param {number} amount - Payment amount
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<{checkoutSession: string, merchantOrderId: string}>} Checkout session details
 * @throws {Error} If session creation fails or response is invalid
 */
const createYunoCheckoutSession = async (purchaseUrl, purchaseToken, amount, dispatch) => {
  try {
    yuno.initialize(purchaseUrl);
    const sessionData = await yuno.createCheckoutSession({
      purchaseToken,
      amount,
    });
    return sessionData;
  } catch (error) {
    dispatch(setError(205, 'error_creating_yuno_session', 'error', false));
    throw new Error(`Error in createYunoCheckoutSession: ${error.message}`);
  }
};

/** Retrieves session data for a Yuno checkout
 * @param {string} purchaseUrl - Base URL for the purchase API
 * @param {string} checkoutSession - Yuno checkout session identifier
 * @param {string} purchaseToken - Token identifying the purchase
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Available payment methods and session data
 * @throws {Error} If data retrieval fails or response is invalid
 */
const getYunoSessionData = async (purchaseUrl, checkoutSession, purchaseToken, dispatch) => {
  try {
    yuno.initialize(purchaseUrl);
    const { availableMethods } = await yuno.getSessionData({
      checkoutSession,
      purchaseToken,
    });
    return availableMethods;
  } catch (error) {
    dispatch(setError(205, 'error_getting_yuno_session_data', 'error', false));
    throw new Error(`Error in getYunoSessionData: ${error.message}`);
  }
};

/** Fetches Yuno payment integration data
 * @param {object} params - Parameters for fetching Yuno data
 * @param {string} params.purchaseUrl - Base URL for the purchase API
 * @param {string} params.purchaseToken - Token identifying the purchase
 * @param {number} params.amount - Payment amount
 * @returns {Function} Redux thunk action creator
 */
export function fetchYunoData({ purchaseUrl, purchaseToken, amount }) {
  return async (dispatch) => {
    try {
      const { checkoutSession, merchantOrderId } = await createYunoCheckoutSession(
        purchaseUrl,
        purchaseToken,
        amount,
        dispatch,
      );
      const availableMethods = await getYunoSessionData(
        purchaseUrl,
        checkoutSession,
        purchaseToken,
        dispatch,
      );
      const yunoSessionData = {
        checkoutSession,
        merchantOrderId,
        availableMethods,
      };
      dispatch({ type: types.SET_YUNO_SESSION_DATA, yunoSessionData });
    } catch (error) {
      dispatch(setError(205, 'error_fetching_yuno_data', 'error', false));
      throw new Error(`Error on paymentPayload for yunoPaymentAttempt: ${error.message}`);
    }
  };
}

/**
 * Sync seats from seatsBus to purchase state
 * @param {string} departureId - Departure ID
 * @param {string} returnId - Return ID
 * @returns {Function} Dispatch action
 */
export function syncSeatsFromBus(departureId, returnId) {
  return (dispatch, getState) => {
    const {
      seatsBus: seatsBusState,
      whitelabelConfig: { features },
    } = getState();

    const { USE_SEAT_BASED_PRICING: useSeatBasedPricing } = features;
    if (!useSeatBasedPricing) return;
    const seatsBus = seatsBusState.toJS();

    if (departureId && seatsBus[departureId]?.selectedSeats?.length) {
      dispatch(selectSeats('departs', seatsBus[departureId].selectedSeats));
    }
    if (returnId && seatsBus[returnId]?.selectedSeats?.length) {
      dispatch(selectSeats('returns', seatsBus[returnId].selectedSeats));
    }
  };
}

/**
 * Request a promotional code
 * @returns {Object} Dispatch action
 */
export function requestPromotionalCode() {
  return { type: types.REQUEST_PROMO_CODE };
}

/**
 * Receive a promotional code
 * @param {boolean} success - Whether the promotional code was successfully applied
 * @returns {Object} Dispatch action
 */
export function receivePromotionalCode(success) {
  return { type: types.RECEIVE_PROMO_CODE, success };
}

/**
 * Apply a promotional code to the purchase
 * @param {string} token - Purchase token
 * @param {string} code - Promotional code
 * @returns {Promise} Promise that resolves with the result
 */
export function applyPromotionalCode(token, code) {
  return (dispatch) => {
    dispatch(requestPromotionalCode());
    dispatch({ type: types.PROMO_CODE_SUBMITTING, isSubmitting: true });

    return purchaseApi
      .validatePromotionalCode(token, code)
      .then((payload) => {
        const { success } = payload;
        dispatch(receivePromotionalCode(success));

        if (!success) {
          dispatch(setError(206, 'invalid_promo_code', 'error', false, payload.message));
        }

        return { success, message: payload.message };
      })
      .catch((reason) => {
        dispatch(receivePromotionalCode(false));

        dispatch(
          setError(
            206,
            reason.code === 500 ? 'error_when_applying_promo_code' : 'invalid_promo_code',
            'error',
            false,
          ),
        );
        throw reason;
      })
      .finally(() => {
        dispatch({ type: types.PROMO_CODE_SUBMITTING, isSubmitting: false });
      });
  };
}
